import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { printingLabelFromKey, printingColor, printingTextColor } from '../components/wish/printings';
import { offerPrice } from '../utils/dealHandoff';
import { isPricingVisible } from '../utils/pricingGate';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { sizedImageUrl, IMG } from '../utils/imageUrl';
import { useAuth } from '../context/AuthContext';
import { useGuestPrompt } from '../context/GuestPromptContext';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { getVariantOptions, variantLabel, labelFor } from '../services/cardData/variantOptions';
import { sendTradeRequestNotification } from '../lib/notifications';
import { getNavigationInfo, updateContextIndex, clearCardNavigationContext, getNavCount, setCardNavigationContext } from '../lib/cardNavigationContext';
import { getCardPriceRow, getPriceForCard, priceDetailForCard, getPriceDetails, isRefreshLocked, bumpPriceReads, daysSinceRefresh,
         fetchAndCacheSingleCard, isGradedCard, formatPrice,
         asksAreFresh, cacheAsks } from '../services/cardPriceCache';
import { sourceForSet, SOURCE_LABELS } from '../services/priceSource';
import { isVintageSet } from '../utils/vintageDetection';
import {
  TOP_TEN_LIMIT,
  TOP_TEN_LABEL,
  topTenFullLabel,
  isTopTenFull,
  isTopTenCapError,
} from '../services/wishList';
import { PC_ONLY } from '../services/wishBinders';
import { TRADE_BINDER_EXPLAINER, TRADE_BINDER_MOVE_NOTE, TRADE_NEEDS_BINDER_WARNING, isTradeBinder } from '../services/tradeBinder';
import StartChatSheet from '../components/trade/StartChatSheet';
import { requestChat, openRequestWith } from '../services/tradeRequest';
import CreatePostModal from '../components/modals/CreatePostModal';
import TutorialOverlay, { TutorialBackdrop } from '../components/TutorialOverlay';
import AddParentModal from '../components/AddParentModal';
import ChaseWaveImage from '../assets/images/ChaseWave.png';
import CardImage from '../components/CardImage';
import CardChaseLogo from '../assets/logo.png';
import haptics from '../utils/haptics';
import { ebaySoldUrl, tcgplayerUrl } from '../utils/cardLinks';
import PriceLadder from '../components/PriceLadder';
import PriceHistory from '../components/pricing/PriceHistory';
import { tiersFromStats } from '../utils/tiersFromStats';
import AskChase from '../components/chase/AskChase';
import ChaseChat from '../components/chase/ChaseChat';
import { threadExists } from '../utils/chaseService';
import { getAllPriceTiers, getSourcesForCard } from '../utils/poketraceService';
import SaleBreakdownSheet from '../components/pricing/SaleBreakdownSheet';
import { fetchHeldOut } from '../utils/saleReports';
import ReportPricingSheet from '../components/ReportPricingSheet';
import BottomSheet from '../components/BottomSheet';
import { buildReportContext } from '../utils/pricingReports';
import { TcgPocketBanner, TcgPocketLine } from '../components/TcgPocketNotice';
import {
  isTcgPocketCard,
  isTcgPocketCardId,
  useTcgPocketSets,
  TCG_POCKET_LABEL,
  TCG_POCKET_TRADE_REASON,
  TCG_POCKET_TRADE_SHORT,
  TCG_POCKET_FREE_TITLE,
  TCG_POCKET_FREE_BODY,
} from '../utils/tcgPocket';
import * as cardDataApi from '../services/cardData';

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M11 8v6" />
    <path d="M8 11h6" />
  </svg>
);

const GridIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const MaximizeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = ({ style }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronLeftIcon = ({ size = 32, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, width: size, height: size }}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = ({ size = 32, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, width: size, height: size }}>
    <polyline points="9 6 15 12 9 18" />
  </svg>
);


const ShareIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const FolderIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const TradeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

const InfoIcon = ({ style }) => (
  <svg style={style} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const HeartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const HeartFilledIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ClockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MoreVerticalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1.5"></circle>
    <circle cx="12" cy="12" r="1.5"></circle>
    <circle cx="12" cy="19" r="1.5"></circle>
  </svg>
);

// Card Options Menu Component
const CardOptionsMenu = ({ isOpen, onClose, isFollowing, onToggleFollow, onReportCard, onReportUser, onBlock, isOwnCard, theme }) => {
  if (!isOpen) return null;

  const m = menuStyles(theme);

  return (
    <>
      <div style={m.backdrop} onClick={onClose} />
      <div style={m.menu}>
        {!isOwnCard && (
          <>
            <button style={m.menuItem} onClick={() => { onToggleFollow(); onClose(); }}>
              <span style={m.menuItemText}>
                {isFollowing ? 'Unfollow User' : 'Follow User'}
              </span>
            </button>
            <div style={m.menuDivider} />
            <button style={m.menuItem} onClick={() => { onBlock(); onClose(); }}>
              <span style={m.menuItemText}>Block User</span>
            </button>
            <div style={m.menuDivider} />
          </>
        )}
        <button style={m.menuItemDanger} onClick={() => { onReportCard(); onClose(); }}>
          <span style={m.menuItemTextDanger}>Report Card</span>
        </button>
      </div>
    </>
  );
};

// Report Card Modal Component
const ReportCardModal = ({ isOpen, onClose, cardId, currentUserId, onSuccess, theme }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { id: 'nsfw', label: 'NSFW Content', description: 'Adult or inappropriate content' },
    { id: 'wrong_content', label: 'Wrong Content', description: 'Not Pokemon/TCG related' },
    { id: 'spam', label: 'Spam', description: 'Repetitive or promotional content' },
    { id: 'scam', label: 'Scam', description: 'Fraudulent or deceptive content' },
    { id: 'other', label: 'Other', description: 'Another issue not listed' },
  ];

  const handleSubmit = async () => {
    if (!selectedReason || !currentUserId || !cardId) return;

    setSubmitting(true);
    try {
      // Insert report with pending status
      const { error } = await supabase
        .from('reports')
        .insert({
          reporter_id: currentUserId,
          reported_card_id: cardId,
          reason: selectedReason,
          description: description.trim() || null,
          status: 'pending',
        });

      if (error) {
        if (error.code === '23505') {
          alert('You have already reported this card.');
        } else {
          throw error;
        }
      } else {
        // Hide the card immediately for safety (admin will review)
        await supabase
          .from('cards')
          .update({ hidden: true })
          .eq('id', cardId);

        onSuccess && onSuccess();
        onClose();
        setSelectedReason(null);
        setDescription('');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const s = reportStyles(theme);

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h2 style={s.title}>Report Card</h2>
          <button style={s.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div style={s.content}>
          <p style={s.subtitle}>Why are you reporting this card?</p>

          <div style={s.reasonsList}>
            {reasons.map((reason) => (
              <button
                key={reason.id}
                style={{
                  ...s.reasonButton,
                  ...(selectedReason === reason.id ? s.reasonButtonSelected : {}),
                }}
                onClick={() => setSelectedReason(reason.id)}
              >
                <div style={s.reasonContent}>
                  <span style={{
                    ...s.reasonLabel,
                    ...(selectedReason === reason.id ? s.reasonLabelSelected : {}),
                  }}>{reason.label}</span>
                  <span style={s.reasonDescription}>{reason.description}</span>
                </div>
                <div style={{
                  ...s.radioOuter,
                  ...(selectedReason === reason.id ? s.radioOuterSelected : {}),
                }}>
                  {selectedReason === reason.id && <div style={s.radioInner} />}
                </div>
              </button>
            ))}
          </div>

          <div style={s.descriptionSection}>
            <label style={s.descriptionLabel}>Additional details (optional)</label>
            <textarea
              style={s.descriptionInput}
              placeholder="Tell us more about the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div style={s.footer}>
          <button style={s.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...s.submitButton,
              ...((!selectedReason || submitting) ? s.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

const menuStyles = (theme) => ({
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    overflow: 'hidden',
    minWidth: '150px',
    zIndex: 1000,
    boxShadow: theme.shadowCard || '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  menuItem: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemDanger: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemText: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
  },
  menuItemTextDanger: {
    color: theme.error,
    fontSize: '14px',
    fontWeight: '500',
  },
  menuDivider: {
    height: '1px',
    backgroundColor: theme.border,
  },
});

const reportStyles = (theme) => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: theme.bgSheet,
    borderRadius: '16px',
    border: `1px solid ${theme.border}`,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  title: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  content: {
    padding: '20px',
  },
  subtitle: {
    color: theme.textMuted,
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reasonButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
  },
  reasonButtonSelected: {
    backgroundColor: theme.accentBg,
    borderColor: theme.accent,
  },
  reasonContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  reasonLabel: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
  },
  reasonLabelSelected: {
    color: theme.accent,
  },
  reasonDescription: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  radioOuter: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: `2px solid ${theme.borderHover}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioOuterSelected: {
    borderColor: theme.accent,
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: theme.accent,
  },
  descriptionSection: {
    marginTop: '20px',
  },
  descriptionLabel: {
    display: 'block',
    color: theme.textMuted,
    fontSize: '13px',
    marginBottom: '8px',
  },
  descriptionInput: {
    width: '100%',
    padding: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.text,
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: `1px solid ${theme.border}`,
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.borderHover}`,
    borderRadius: '10px',
    color: theme.textMuted,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: theme.error,
    border: 'none',
    borderRadius: '10px',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    backgroundColor: theme.textDisabled,
    cursor: 'not-allowed',
  },
});

/**
 * The catalogue's entry for one card, or nothing.
 *
 * Nothing on this screen waits on it and nothing on this screen fails without
 * it, so it never throws and it never hangs. The bare fetch did both: tcgdex
 * answers 404 for whole modern sets, and while it was down entirely the request
 * simply never settled, which left the card screen on a spinner that would not
 * end.
 */
const CATALOGUE_TIMEOUT_MS = 6000;

/**
 * The era line under a card's set. Era names already end in "Era" ("e-Reader
 * Era", "Wizards of the Coast Era (1999-2003)"), and appending the word again
 * printed "e-Reader Era era" on every card.
 */
const eraLine = (name) => (/\bera\b/i.test(name) ? name : `${name} era`);

/**
 * Our own catalogue first, TCGdex only as the fallback the router already
 * provides. This used to go straight to api.tcgdex.net, which answered
 * "Card not found" for every one of the 6,092 cards in the Collectr-sourced
 * sets (the Shadowless Charizard at the top of a "char 4" search among them)
 * and put an outside round trip in front of every detail screen. The answer
 * comes back in TCGdex's shape so the screen below reads it unchanged, except
 * that `image` is a finished URL rather than a base path.
 */
async function fetchCatalogueCard(tcgdexCardId) {
  const timeout = new Promise((resolve) => setTimeout(() => resolve(null), CATALOGUE_TIMEOUT_MS));
  try {
    const card = await Promise.race([
      cardDataApi.getCard(tcgdexCardId, { language: 'en' }).catch(() => null),
      timeout,
    ]);
    if (!card || !card.name) return null;
    return {
      name: card.name,
      set: { name: card.set_name || card.set?.name || null },
      image: card.image_url || null,
      hp: card.hp ?? null,
      types: card.types ?? null,
      rarity: card.rarity ?? null,
    };
  } catch {
    return null;
  }
}

const CardDetail = () => {
  const { cardId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { requireAuth } = useGuestPrompt();
  const { searchExactCard, searchCardByName, setHideNav } = useUI();
  const { currentStep, handleTradeRequestSent, handleFirstTradeRequestNext } = useTutorial();
  const { isViewerOnly, activeProfile, activeProfileId, userProfile } = useProfile();
  const { theme, isDark } = useTheme();
  const [showParentModal, setShowParentModal] = useState(false);

  // Check for URL params to open photo viewer - read immediately for initial state
  const viewParam = searchParams.get('view'); // 'zoom' or 'grid'
  const photoParam = searchParams.get('photo'); // index or 'back'

  const [card, setCard] = useState(null);
  const [cardOwner, setCardOwner] = useState(null);
  // Tapping the card name opens this instead of the old Find More button.
  const [namePickerOpen, setNamePickerOpen] = useState(false);
  const [priceTiers, setPriceTiers] = useState(null);
  const [priceSources, setPriceSources] = useState(null);
  const [priceTiersFailed, setPriceTiersFailed] = useState(false);
  const [breakdownTier, setBreakdownTier] = useState(null);
  // Reported and personally excluded sales, owned at this level rather than
  // inside the breakdown sheet. The ladder behind the sheet and the card's own
  // price both have to move when one is tapped, and they cannot read state
  // that lives in a child.
  const [heldOut, setHeldOut] = useState(null);
  const [breakdownSource, setBreakdownSource] = useState('eBay sold');

  // Spreads the existing preferences rather than replacing them, so saving a
  // sale window here cannot wipe the modern range set on the settings screen.
  const saveSaleWindow = async (next) => {
    setPricingPrefs((prev) => ({ ...(prev || {}), sale_window: next }));
    if (!activeProfile?.id) return;
    await supabase
      .from('users')
      .update({ pricing_prefs: { ...(activeProfile.pricing_prefs || {}), sale_window: next } })
      .eq('id', activeProfile.id);
  };
  const [reportSheetOpen, setReportSheetOpen] = useState(false);
  // TCGplayer asking prices, which only the Raw tab shows. Fetching them on
  // arrival cost a second full Poketrace lookup on the critical path - the same
  // one the tier fetch was already doing - to fill a column a graded card never
  // displays. Now they are asked for the first time Raw is actually looked at.
  const rawSourcesRef = useRef(false);
  const requestRawSources = useCallback(() => {
    if (rawSourcesRef.current || !card?.name) return;
    rawSourcesRef.current = true;
    // Bounded, because null means "still asking" and the TCGplayer column
    // shimmers while it is. This call chains a full vendor lookup into two
    // more, and when one of them never answers the column shimmered for ever
    // with nothing to tell the owner it had given up.
    const settled = { done: false };
    const finish = (srcs) => {
      if (settled.done) return;
      settled.done = true;
      setPriceSources(srcs || {});
    };
    setTimeout(() => finish({}), 20000);

    getSourcesForCard({
      cardName: card.name,
      cardNumber: card.set_number,
      setName: card.set_name,
      tcgdexCardId: card.card_id,
      variant: card.variant,
      // Already resolved by the ladder. Without this the TCGplayer column pays
      // for the same vendor lookup a second time before it can even start.
      knownTcgplayerId: priceTiers?.tcgplayerId || null,
    })
      .then((srcs) => {
        finish(srcs);
        // Kept, so the next visit to this card does not pay for it again.
        const asks = srcs?.justTCG?.conditions;
        if (asks && Object.keys(asks).length) {
          cacheAsks({
            cardName: card.name,
            setName: card.set_name || '',
            setNumber: card.set_number || '',
            variant: card.variant || '',
          }, asks).catch(() => {});
        }
      })
      .catch(() => finish({}));
    // priceTiers is a dependency because the tcgplayerId rides on it. Without
    // it this closure keeps the value from before the ladder resolved, which is
    // null, and the shortcut never applies.
  }, [card?.name, card?.set_number, card?.set_name, card?.card_id, card?.variant,
    priceTiers?.tcgplayerId]);

  // True once the vendor has answered, so a slow cache seed cannot land on top
  // of a better number that already arrived.
  const vendorTiersRef = useRef(false);
  // The in-flight vendor lookup for this card, shared.
  //
  // The ladder and the individual sales are fetched by two different effects,
  // and both used to resolve the card at the vendor from scratch: two searches
  // and two pricing calls for one card. Holding the promise lets the second one
  // wait for the first instead of repeating it.
  const tiersPromiseRef = useRef(null);

  // Which row of the ladder is selected. PriceLadder reports it, the price
  // history chart above the section draws it, so the picture and the number can
  // never disagree about which tier is being read.
  const [selectedTier, setSelectedTier] = useState(null);
  // The chart is the only thing in the row that opens in place. Chase opens a
  // popover instead, so the two cannot fight over the same vertical space.
  const [historyOpen, setHistoryOpen] = useState(false);
  // Which door opened the chat, and null when it is closed. 'resume' means an
  // existing thread was reopened and no question should be fired into it.
  const [chatDoor, setChatDoor] = useState(null);
  // Whether this card already has a conversation. Asked once on arrival so the
  // tap can go straight in rather than offering the three doors again.
  const [hasThread, setHasThread] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHasThread(false);
    if (!card?.name) return undefined;
    threadExists(card).then((yes) => { if (!cancelled) setHasThread(yes); });
    return () => { cancelled = true; };
  }, [card?.name, card?.set_name, card?.set_number]);

  // The preferences screen does not exist yet, so the tap gets an answer and
  // takes itself away rather than leaving a dead toast on the card.
  useEffect(() => {
    if (!comingSoon) return undefined;
    const timer = setTimeout(() => setComingSoon(false), 1900);
    return () => clearTimeout(timer);
  }, [comingSoon]);

  // "Holo / 1st Edition Stamp" is stored joined and shown apart.
  const variantParts = useMemo(
    () => String(card?.variant || '').split('/').map((v) => v.trim()).filter(Boolean),
    [card?.variant],
  );

  /**
   * A printing wears the same colour here as it does on a grid tile.
   *
   * Shadowless is a blue badge in Explore, in a vendor deal and in a search;
   * it was grey text here, on the one screen where somebody is deciding what
   * the card in their hand actually is. One colour per printing, everywhere.
   */
  // The printing this screen was opened on, from the tile that sent us.
  const viewingPrinting = printingLabelFromKey(searchParams.get('printing')) || null;

  const chipTone = useCallback((part) => {
    const text = String(part || '');
    // The finish keeps its holo yellow; it is not a printing.
    if (/^(holo|reverse|reverse holo|normal|foil)$/i.test(text)) return null;
    const colour = printingColor(text, [], null);
    return colour ? { background: colour, color: printingTextColor(text, [], null), border: 0 } : null;
  }, []);

  // False until a card has rendered once. Distinguishes the full-screen spinner
  // on arrival from the body-only swap when stepping through a binder or set.
  const hasLoadedOnceRef = useRef(false);
  // Neighbour card rows, fetched quietly once the current card has settled, so
  // the next arrow tap paints from memory instead of a round trip.
  const prefetchedRef = useRef(new Map());
  const [cardEra, setCardEra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOwnCard, setIsOwnCard] = useState(false);
  // Initialize photo viewer state directly from URL params to avoid flash
  const [photoViewerOpen, setPhotoViewerOpen] = useState(!!viewParam);
  const [photoViewMode, setPhotoViewMode] = useState(viewParam === 'grid' ? 'grid' : 'zoom');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isPinchZoomed, setIsPinchZoomed] = useState(false);
  const [initialViewHandled, setInitialViewHandled] = useState(false);
  const [cameFromExternal, setCameFromExternal] = useState(!!viewParam); // Track if opened via URL params
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportSuccessOpen, setReportSuccessOpen] = useState(false);
  const [binderPickerOpen, setBinderPickerOpen] = useState(false);
  // The (i) next to the trade chip, and the one-line answer when the rule
  // blocks something. Both render in the same slot under the chip row.
  const [showTradeBinderInfo, setShowTradeBinderInfo] = useState(false);
  const [binderRuleNote, setBinderRuleNote] = useState('');
  const [binders, setBinders] = useState([]);
  const [cardBinder, setCardBinder] = useState(null);
  const [selectedBinder, setSelectedBinder] = useState(null);
  const [previewBinder, setPreviewBinder] = useState(null);
  const [previewCards, setPreviewCards] = useState([]);
  const [userCards, setUserCards] = useState([]); // All user's cards for post modal
  const [cardPrice, setCardPrice] = useState(null);
  const [priceDetailsInfo, setPriceDetailsInfo] = useState(null);
  const [priceRow, setPriceRow] = useState(null);
  // Whether the individual-sales fetch has finished, successfully or not. Not
  // the same as priceRow being set: a card with no cached row settles too.
  const [evidenceReady, setEvidenceReady] = useState(false);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const pricingEnabled = isPricingVisible(activeProfile);
  const [pricingPrefs, setPricingPrefs] = useState(activeProfile?.pricing_prefs || { modern_range: '3m', vintage_method: 'top3' });
  const [showPrefsPicker, setShowPrefsPicker] = useState(false);

  // The profile usually lands after this screen has mounted, and the price
  // source cut lives in these prefs. Seeded once and never updated, the card
  // screen priced off the default cut while the collection tile behind it used
  // the owner's, which is the disagreement the cut exists to remove.
  //
  // saveSaleWindow's optimistic update survives it: that writes to the database
  // without refreshing the profile, so this object does not change and the
  // effect does not fire.
  useEffect(() => {
    if (activeProfile?.pricing_prefs) setPricingPrefs(activeProfile.pricing_prefs);
  }, [activeProfile?.pricing_prefs]);

  const VINTAGE_LABELS = { top3: 'Top 3 (12mo)', low3: 'Low 3 (12mo)', '12avg': '12 Month Median', alltime: 'All Time Median' };
  const MODERN_LABELS = { '1m': '1 Month Median', '3m': '3 Month Median', '6m': '6 Month Median', '12m': '12 Month Median' };

  // Trade request state
  const [tradeRequestSent, setTradeRequestSent] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [tradeConfirmOpen, setTradeConfirmOpen] = useState(false);
  // Their trade binder, offered in the confirmation. Offered, not explained.
  const [ownerTradeBinder, setOwnerTradeBinder] = useState(null);

  // Post/Repost state
  const [lastPostDate, setLastPostDate] = useState(null);
  const [daysUntilRepost, setDaysUntilRepost] = useState(0);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [gradePickerOpen, setGradePickerOpen] = useState(false);
  const [savingGrade, setSavingGrade] = useState(false);
  const [gradeType, setGradeType] = useState('raw'); // 'raw' | 'graded'
  const [gradingCompany, setGradingCompany] = useState('PSA');
  const [customCompany, setCustomCompany] = useState(''); // For "Other" grading company
  const [selectedGrade, setSelectedGrade] = useState(null); // Pending selection before confirm

  // Variant picker state
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [savingVariant, setSavingVariant] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  // The axes the catalogue says this card has, and the draft being edited.
  const [variantAxes, setVariantAxes] = useState(null);
  const [variantDraft, setVariantDraft] = useState({ type: null, subtype: null, stamps: [] });

  // TCGdex/Wish List mode
  const isTcgCard = cardId?.startsWith('tcg_');
  // TCG Pocket is the digital-only game. Those cards were never printed, so no
  // price exists to fetch and none ever will. Known from the route id before
  // the card row even lands, so the pricing section never flashes a skeleton
  // that is waiting on nothing.
  useTcgPocketSets();
  const isPocketCard = isTcgPocketCardId(cardId) || isTcgPocketCard(card);
  // Set by a screen that already knows the printing, such as a vendor deal.
  const previewHints = location.state?.preview || null;

  /**
   * Opened from a deal, to be read out loud across a table.
   *
   * Everything about the pricing stays - every eBay sale, every TCGplayer ask,
   * every condition, and the way down into the individual sales. What goes is
   * anything that changes the card: the pickers, the trade status, posting. A
   * vendor is showing a customer why a number is what it is, not editing their
   * collection in front of them.
   */
  const dealMode = location.state?.deal || null;

  // Reviewing a card for a deal, the bar at the bottom is the way back and the
  // tabs would only lead away from the table. Everywhere else the nav stays,
  // except while a sheet is up, when it steps out of the sheet's way.
  useEffect(() => {
    if (!dealMode && !gradePickerOpen) return undefined;
    setHideNav(true);
    return () => setHideNav(false);
  }, [dealMode, gradePickerOpen, setHideNav]);
  const canEdit = isOwnCard && !dealMode;
  // The catalogue route was built for the wish list, so it renders a browsing
  // page: a "Wish List" badge where the card's own details go. Opened from a
  // deal, or from pricing a scan, it has to be the card's real screen instead -
  // the identity, and every price under it - which is what the same screen
  // shows once those cards are in a collection.
  const browsingOnly = isTcgCard && !dealMode && !previewHints;
  // Pre-filled with whatever this screen currently says the card is worth, and
  // editable, because the number that comes back is the one the two of them
  // settled on rather than the one the market last printed.
  const [dealFigure, setDealFigure] = useState('');
  useEffect(() => {
    if (!dealMode) return;
    const p = cardPrice?.price;
    if (typeof p === 'number' && p > 0) setDealFigure(String(p.toFixed(2)));
  }, [dealMode, cardPrice?.price]);

  const refreshHeldOut = useCallback(() => {
    if (!card?.name) return;
    fetchHeldOut(card).then((r) => setHeldOut({
      reported: new Set([...r.mine, ...r.consensus]),
      excluded: r.excluded,
    }));
  }, [card]);

  useEffect(() => { refreshHeldOut(); }, [refreshHeldOut]);

  // The card's own price obeys the same held-out set the ladder does.
  //
  // Its own effect rather than a line inside the fetch: excluding a comp has to
  // move this number, and re-running the fetch to get that would spend a read
  // on a row already in state.
  useEffect(() => {
    if (!priceRow) return;
    // The detail rather than the bare number, because this screen names the
    // market the headline came from and the owner's cut decides which of the
    // two was asked first. See services/priceSource.
    const detail = priceDetailForCard(
      priceRow.stats, card?.grade || 'NM', card?.set_name, pricingPrefs,
      priceRow.evidence, heldOut, priceRow.asks,
    );
    if (detail?.value != null) {
      setCardPrice({ price: detail.value, source: detail.source, estimated: detail.estimated });
    }
  }, [priceRow, heldOut, card?.grade, card?.set_name, pricingPrefs]);

  // The market this card's set is set to price from, which is not always the
  // one the headline came from: a preferred source with nothing for this
  // condition hands over to the other rather than showing nothing. Saying which
  // happened is the difference between a number and a number you can trust.
  const preferredSource = sourceForSet(card?.set_name, pricingPrefs);
  const [isWished, setIsWished] = useState(false);
  const [wishListCount, setWishListCount] = useState(0);
  const [addingToWishList, setAddingToWishList] = useState(false);
  const [removingFromWishList, setRemovingFromWishList] = useState(false);

  // Card navigation context (for left/right arrows between cards)
  const [navInfo, setNavInfo] = useState({ hasPrev: false, hasNext: false, currentIndex: -1, total: 0 });

  /**
   * A card arrived at from outside the app, with the binder named on the URL.
   *
   * The arrows read a list the app puts in sessionStorage when you tap a card in
   * a grid. Somebody scanning a printed label has never been in a grid, so there
   * is nothing there and they land on an island: no Prev, no Next, and a back
   * button with no history behind it.
   *
   * So the binder on the URL builds that list. Every card in it is readable by a
   * stranger already - the database allows any card that is not private, hidden
   * or deleted - so this needs no account and no new screen, and the ordinary
   * navigation works exactly as it does for somebody who tapped in.
   *
   * Only when there is no list already. Somebody who DID tap in from somewhere
   * keeps the list they came from.
   */
  const linkBinderId = searchParams.get('b');
  // Which printing was tapped, from the grid that sent us. A card has several
  // and they are worth very different money, so arriving on "a Charizard" when
  // somebody tapped the Shadowless one is the wrong screen. Absent means the
  // card itself, which is what every link made before this did.
  const printingParamKey = searchParams.get('printing');
  useEffect(() => {
    if (!linkBinderId || !cardId) return undefined;
    if (getNavigationInfo(cardId).total > 0) return undefined;

    let alive = true;
    (async () => {
      const [{ data: rows }, { data: binder }] = await Promise.all([
        supabase
          .from('cards')
          .select('id, created_at')
          .eq('binder_id', linkBinderId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false }),
        supabase.from('binders').select('id, name').eq('id', linkBinderId).maybeSingle(),
      ]);
      if (!alive || !rows?.length) return;
      const ids = rows.map((r) => r.id);
      const index = ids.indexOf(cardId);
      if (index === -1) return;
      setCardNavigationContext({
        type: 'binder',
        cardIds: ids,
        currentIndex: index,
        sourceId: linkBinderId,
        sourceLabel: binder?.name || 'this binder',
      });
      setNavInfo(getNavigationInfo(cardId));
    })();
    return () => { alive = false; };
  }, [linkBinderId, cardId]);

  // Update navigation info when card changes
  useEffect(() => {
    // A card opened from a deal is one card, not a place in a list. A stale
    // collection context could otherwise put Prev and Next on it and walk the
    // vendor out of the deal.
    if (dealMode) return;
    if (cardId) {
      const info = getNavigationInfo(cardId);
      setNavInfo(info);
    }
  }, [cardId, dealMode]);

  // Warm the neighbours once this card has settled.
  //
  // Stepping through a binder is a sequence, and the next card is knowable
  // before it is asked for. Fetching its row and priming the price cache while
  // the user is still reading this one turns the arrow tap into a render rather
  // than a round trip.
  //
  // Deliberately not reported to the pricing repair loop: a prefetch that fails
  // is a card nobody looked at, and counting it would inflate the queue with
  // problems no user has actually hit.
  useEffect(() => {
    if (loading || !card || isTcgCard) return undefined;
    let cancelled = false;

    const warm = async (id) => {
      if (!id || prefetchedRef.current.has(id)) return;
      prefetchedRef.current.set(id, true);
      try {
        const { data } = await supabase.from('cards').select('*').eq('id', id).single();
        if (cancelled || !data) return;
        prefetchedRef.current.set(id, data);
        // Priming the shared price cache is the expensive half; the ladder on
        // the next card then reads it straight out of storage. A TCG Pocket
        // neighbour is skipped: it is free, it will never show a ladder, and
        // asking the pricing stack about it only spends a lookup.
        if (pricingEnabled && data.name && !isTcgPocketCard(data)) {
          await getAllPriceTiers({
            cardName: data.name,
            cardNumber: data.set_number,
            setName: data.set_name,
            variant: data.variant,
          });
        }
      } catch {
        /* a cold neighbour is not an error, it just stays cold */
      }
    };

    // Sequential and after a beat, so this never competes with the card the
    // user is actually looking at.
    const timer = setTimeout(async () => {
      await warm(navInfo.nextId);
      if (!cancelled) await warm(navInfo.prevId);
    }, 400);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [loading, card, isTcgCard, navInfo.nextId, navInfo.prevId, pricingEnabled]);

  // Navigate to previous/next card
  const handleNavigatePrev = () => {
    if (navInfo.hasPrev && navInfo.prevId) {
      updateContextIndex(navInfo.currentIndex - 1);
      // Preserve any URL params like view or photo
      const currentParams = searchParams.toString();
      navigate(`/card/${navInfo.prevId}${currentParams ? `?${currentParams}` : ''}`);
    }
  };

  const handleNavigateNext = () => {
    if (navInfo.hasNext && navInfo.nextId) {
      updateContextIndex(navInfo.currentIndex + 1);
      // Preserve any URL params like view or photo
      const currentParams = searchParams.toString();
      navigate(`/card/${navInfo.nextId}${currentParams ? `?${currentParams}` : ''}`);
    }
  };

  /**
   * What the counter calls the list the arrows are walking.
   *
   * The list's own name when it has one - "Good Ol Lappy", "Stellar Crown" -
   * because that is what somebody recognises. The kind of place is the fallback,
   * for a list that never named itself.
   *
   * It must come from the CONTEXT and not from the card. The counter used to
   * decide the word by asking whether this card happened to sit in a binder,
   * which has nothing to do with where it was opened from: a card in a binder,
   * opened from a set, read "in binder" over a count of the set.
   */
  const getNavContextLabel = () => {
    if (navInfo.sourceLabel) return navInfo.sourceLabel;
    switch (navInfo.contextType) {
      case 'collection':
        return 'this collection';
      case 'collection-set':
      case 'wishlist-set':
        return 'this set';
      case 'home-post':
        return 'this post';
      case 'wishlist':
        return 'the wish list';
      case 'binder':
        return 'this binder';
      default:
        return null;
    }
  };

  // Swipe handling for photo viewer
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (cardPhotos) => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0 && selectedPhotoIndex < cardPhotos.length - 1) {
        // Swiped left - next photo
        setSelectedPhotoIndex(prev => prev + 1);
      } else if (diff < 0 && selectedPhotoIndex > 0) {
        // Swiped right - previous photo
        setSelectedPhotoIndex(prev => prev - 1);
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const goToPrevPhoto = () => {
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(prev => prev - 1);
    }
  };

  const goToNextPhoto = (cardPhotos) => {
    if (selectedPhotoIndex < cardPhotos.length - 1) {
      setSelectedPhotoIndex(prev => prev + 1);
    }
  };

  // Search for this exact card (name + set + number)
  // Era for the facts column. Cheap: two cached adapter reads, and it fails
  // quietly because a missing era should never block the screen.
  useEffect(() => {
    let cancelled = false;
    // The set id is carried on a catalogue card; splitting the card id on a
    // dash breaks for the Collectr-sourced sets ("p-base-set-shadowless-004").
    const setId = card?.tcg_set_id || card?.card_id?.split('-')[0] || null;
    if (!setId) { setCardEra(null); return undefined; }
    (async () => {
      try {
        const set = await cardDataApi.getSet(setId);
        if (!set?.era_id || cancelled) return;
        const eras = await cardDataApi.getEras();
        const era = (eras || []).find((e) => e.id === set.era_id);
        if (!cancelled) setCardEra(era?.display_name || null);
      } catch {
        /* era is decoration, not a dependency */
      }
    })();
    return () => { cancelled = true; };
  }, [card?.card_id]);

  // Pricing ladder data. Refetches on finish change because finish is a
  // different priced card, not a display option (item 29).
  useEffect(() => {
    let cancelled = false;
    // Never ask a vendor about a card that was never printed. It would come
    // back empty every time and each miss would be logged to the pricing
    // repair loop as a problem somebody has to look at.
    if (isPocketCard) { setPriceTiers(null); setPriceTiersFailed(false); return undefined; }
    if (!card?.name) { setPriceTiers(null); return undefined; }

    // Clear the previous card's numbers before fetching this one's. This effect
    // only re-runs when the card's identity changes, so anything still in state
    // belongs to the card you just stepped away from - showing it under a new
    // name for a beat is worse than showing a skeleton.
    setPriceTiers(null);
    setPriceSources(null);
    setPriceTiersFailed(false);
    // Stepping to another card closes what was open on the last one. A chart
    // left expanded would repaint with a different card's sales under the same
    // scroll position, which reads as the number changing on its own.
    setSelectedTier(null);
    setHistoryOpen(false);
    rawSourcesRef.current = false;
    vendorTiersRef.current = false;
    tiersPromiseRef.current = null;
    // Fired together, not one after the other. These are separate vendors with
    // nothing to say to each other, and awaiting the first before starting the
    // second meant the TCGplayer column sat empty for the sum of both calls
    // rather than the longer of the two. Each paints the moment it lands.
    const tiersPromise = getAllPriceTiers({
      cardName: card.name,
      cardNumber: card.set_number,
      setName: card.set_name,
      variant: card.variant,
      // Logs a silent failure to the pricing repair loop, tagged with the gate
      // that rejected it. Fire-and-forget - never blocks or throws.
      reportCard: card,
    });
    tiersPromiseRef.current = tiersPromise;

    tiersPromise.then((tiers) => {
      if (cancelled) return;
      // The vendor is the better answer, so it always wins over the seed. A
      // vendor that found nothing does not: the seed on screen is real cached
      // data and blanking it would be a downgrade.
      if (tiers) { vendorTiersRef.current = true; setPriceTiers(tiers); }
      else setPriceTiersFailed(true);
    }).catch(() => { if (!cancelled) setPriceTiersFailed(true); });

    return () => { cancelled = true; };
  }, [card?.name, card?.set_number, card?.set_name, card?.variant, isTcgCard, isPocketCard]);

  const handleSearchThisCard = () => {
    if (card) {
      searchExactCard({
        name: card.name,
        set_name: card.set_name,
        set_number: card.set_number,
      });
      setNamePickerOpen(false);
      navigate('/home');
    }
  };

  // "Other Suicunes" - same Pokemon, any set, any number.
  const handleSearchByName = () => {
    if (card) {
      searchCardByName({ name: card.name });
      setNamePickerOpen(false);
      navigate('/home');
    }
  };

  useEffect(() => {
    const fetchCard = async () => {
      if (!cardId) return;
      // Only the very first card blanks the screen. After that the chrome stays
      // put and the body swaps, because tapping an arrow is not arriving at a
      // new screen - it is changing what this one is showing.
      setLoading(true);

      try {
        // Check if this is a TCGdex card for wish list
        if (isTcgCard) {
          // Parse tcg_setId_localId format
          const parts = cardId.replace('tcg_', '').split('_');
          const setId = parts.slice(0, -1).join('_');
          const localId = parts[parts.length - 1];
          const tcgdexCardId = `${setId}-${localId}`;

          // What the screen that sent us here already knew, on screen before a
          // single request goes out. The catalogue is a third party that has
          // answered 404 for whole modern sets and, on a bad day, not answered
          // at all; when it did that there was nothing to draw and the page
          // rendered "Card not found" - or sat on a spinner forever - for a
          // card that was named on the row that was tapped.
          if (previewHints?.name) {
            setCard({
              id: cardId,
              name: previewHints.name,
              set_name: previewHints.setName || 'Unknown Set',
              set_number: previewHints.number || localId,
              variant: previewHints.variant || printingLabelFromKey(printingParamKey) || null,
              grade: previewHints.grade || 'NM',
              digital_image_url: previewHints.image || null,
              card_id: previewHints.tcgId || tcgdexCardId,
              tcg_set_id: setId,
              tcg_local_id: localId,
            });
            setLoading(false);
            hasLoadedOnceRef.current = true;
          }

          // Fetch card directly (not entire set) and user data in parallel.
          // The catalogue call is the only one allowed to fail quietly: it adds
          // HP, type and rarity, and nothing on this screen waits on it.
          const [cardRes, wishDataResult, ownedResult] = await Promise.all([
            fetchCatalogueCard(tcgdexCardId),
            activeProfile?.id ? supabase
              .from('wish_list')
              .select('tcgdex_card_id', { count: 'exact' })
              .eq('user_id', activeProfile.id)
              .eq('is_priority', true) : Promise.resolve({ data: null, count: 0 }),
            activeProfile?.id ? supabase
              .from('cards')
              .select('id')
              .eq('user_id', activeProfile.id)
              .eq('card_id', tcgdexCardId)
              .maybeSingle() : Promise.resolve({ data: null }),
          ]);

          const tcgCard = cardRes;

          if (tcgCard && tcgCard.name) {
            setCard({
              id: cardId,
              name: tcgCard.name,
              set_name: tcgCard.set?.name || previewHints?.setName || 'Unknown Set',
              set_number: previewHints?.number || localId,
              // Carried from wherever this was opened, because the catalogue
              // does not know which printing is in someone's hand. The URL is
              // the second source: a tapped tile puts its printing there, so a
              // link that is shared or reopened lands on the same printing.
              variant: previewHints?.variant || printingLabelFromKey(printingParamKey) || null,
              grade: previewHints?.grade || 'NM',
              // No URL is built when the catalogue has no artwork for the card.
              // The constructed path used to stand in for a missing image and
              // it is a guaranteed 404, which is worse than nothing: a non-null
              // value wins the fallback chain, so the card showed a broken link
              // instead of falling through to the owner's own photo.
              digital_image_url: tcgCard.image || previewHints?.image || null,
              hp: tcgCard.hp,
              types: tcgCard.types,
              rarity: tcgCard.rarity,
              card_id: previewHints?.tcgId || tcgdexCardId,
              tcg_set_id: setId,
              tcg_local_id: localId,
            });
          }

          if (activeProfile?.id) {
            setCurrentUserId(activeProfile.id);
            setWishListCount(wishDataResult.count || 0);
            setIsWished(wishDataResult.data?.some(w => w.tcgdex_card_id === tcgdexCardId) || false);
            setIsOwnCard(!!ownedResult.data);
          }

          setLoading(false);
          hasLoadedOnceRef.current = true;
          return;
        }

        // Regular database card - fetch card AND user profile in parallel.
        // A neighbour warmed by the prefetch above is already in memory, which
        // is what makes stepping through a binder feel like turning a page
        // rather than opening a new one.
        const warmed = prefetchedRef.current.get(cardId);
        const cardPromise = (warmed && typeof warmed === 'object')
          ? Promise.resolve({ data: warmed, error: null })
          : supabase.from('cards').select('*').eq('id', cardId).single();

        const [cardResult, profileResult] = await Promise.all([
          cardPromise,
          user ? supabase.from('users').select('id').eq('auth_id', user.id).single() : Promise.resolve({ data: null }),
        ]);

        if (cardResult.error) throw cardResult.error;
        const cardData = cardResult.data;
        const profileData = profileResult.data;

        // Check if card is hidden (under review)
        if (cardData.hidden) {
          setCard({ ...cardData, isHidden: true });
          setLoading(false);
          return;
        }

        // Determine effective user ID
        const finalUserId = profileData
          ? (activeProfileId && activeProfileId !== profileData.id ? activeProfileId : profileData.id)
          : null;

        // Set essential state
        setCard(cardData);
        if (finalUserId) {
          setCurrentUserId(finalUserId);
          setIsOwnCard(cardData.user_id === finalUserId);
        }

        // Fetch ALL secondary data in parallel BEFORE showing UI
        const secondaryQueries = [];

        // The owner. This used to be awaited on its own between the card and
        // everything below it, which made opening a card three chained round
        // trips instead of two. Nothing in this batch needs the owner, so it
        // rides along with the rest. On the Moto a round trip is roughly twice
        // what it costs on an iPhone, which is why the same screen feels
        // instant on one and slow on the other.
        if (cardData.user_id) {
          secondaryQueries.push(
            supabase
              // public_profiles, because a card owner is someone else. The
              // coordinates this used to carry are now a server-side
              // distance_mi, so the round trip count is unchanged.
              .from('public_profiles')
              .select('id, username, avatar_url, location_sharing_enabled, distance_mi')
              .eq('id', cardData.user_id)
              .single()
              .then(({ data }) => ({ type: 'owner', data }))
          );
        }

        // If no profile, the owner is the only thing left to wait on.
        if (!profileData) {
          const anonResults = await Promise.all(secondaryQueries);
          const owner = anonResults.find((r) => r.type === 'owner')?.data;
          if (owner) setCardOwner(owner);
          setLoading(false);
          return;
        }

        // Wish list. The whole list used to come down the wire with an exact
        // count on it, just to answer "is this one card in it". Two head reads
        // ask the same question without downloading a row.
        if (cardData.card_id) {
          secondaryQueries.push(
            supabase
              .from('wish_list')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', finalUserId)
              .eq('is_priority', true)
              .then(({ count }) => ({ type: 'wishlistCount', count }))
          );
          secondaryQueries.push(
            supabase
              .from('wish_list')
              .select('id', { count: 'exact', head: true })
              .eq('user_id', finalUserId)
              .eq('tcgdex_card_id', cardData.card_id)
              .then(({ count }) => ({ type: 'wishlistHas', count }))
          );
        }

        if (cardData.user_id === finalUserId) {
          // Own card - fetch binders and post status
          secondaryQueries.push(
            supabase
              .from('binders')
              .select('*')
              .eq('user_id', finalUserId)
              .match(PC_ONLY)
              .is('deleted_at', null)
              .order('name')
              .then(({ data }) => ({ type: 'binders', data }))
          );

          // The card's own binder is already in the list above, so asking for
          // it again was a whole extra request for a row we had.

          secondaryQueries.push(
            supabase
              .from('posts_cards')
              .select('posts!posts_cards_post_id_fkey(created_at, user_id)')
              .eq('card_id', cardId)
              .order('posts(created_at)', { ascending: false })
              .limit(1)
              .then(({ data }) => ({ type: 'postHistory', data }))
          );
        } else {
          // Not own card - check follow and trade status
          secondaryQueries.push(
            supabase
              .from('user_follows')
              .select('id')
              .eq('follower_id', finalUserId)
              .eq('following_id', cardData.user_id)
              .maybeSingle()
              .then(({ data }) => ({ type: 'follow', data }))
          );

          // Per person, not per card. Once something is open with this owner,
          // every card of theirs says so - the card that was tapped has no
          // more claim on the conversation than any other.
          secondaryQueries.push(
            openRequestWith(profileData.id, cardData.user_id)
              .then((data) => ({ type: 'tradeRequest', data }))
          );
        }

        // Run all secondary queries in parallel, then update state all at once
        const secondaryResults = await Promise.all(secondaryQueries);

        // Process results
        let newBinders = null;
        let newCardBinder = null;
        let newWishListCount = 0;
        let newIsWished = false;
        let newDaysUntilRepost = 0;
        let newLastPostDate = null;
        let newIsFollowing = false;
        let newTradeRequestSent = false;

        let newOwner = null;

        for (const result of secondaryResults) {
          switch (result.type) {
            case 'owner':
              newOwner = result.data;
              break;
            case 'wishlistCount':
              newWishListCount = result.count || 0;
              break;
            case 'wishlistHas':
              newIsWished = (result.count || 0) > 0;
              break;
            case 'binders':
              newBinders = result.data;
              break;
            case 'postHistory':
              if (result.data?.length > 0 && result.data[0].posts) {
                const lastPost = result.data[0].posts;
                if (lastPost.user_id === finalUserId) {
                  newLastPostDate = new Date(lastPost.created_at);
                  const diffDays = Math.floor((Date.now() - newLastPostDate) / (1000 * 60 * 60 * 24));
                  newDaysUntilRepost = Math.max(0, 7 - diffDays);
                }
              }
              break;
            case 'follow':
              newIsFollowing = !!result.data;
              break;
            case 'tradeRequest':
              newTradeRequestSent = !!result.data;
              break;
          }
        }

        if (cardData.binder_id && newBinders) {
          newCardBinder = newBinders.find((b) => b.id === cardData.binder_id) || null;
        }

        // Set all secondary state at once
        if (newOwner) setCardOwner(newOwner);
        setWishListCount(newWishListCount);
        setIsWished(newIsWished);
        if (newBinders) setBinders(newBinders);
        if (newCardBinder) setCardBinder(newCardBinder);
        setLastPostDate(newLastPostDate);
        setDaysUntilRepost(newDaysUntilRepost);
        setIsFollowing(newIsFollowing);
        setTradeRequestSent(newTradeRequestSent);

        // NOW show the UI - all data is ready
        setLoading(false);
      } catch (err) {
        console.error('Error fetching card:', err);
        setLoading(false);
      } finally {
        // Must be a finally. This lived in the catch, so it only ran when the
        // fetch failed - on every successful load the flag stayed false, every
        // card change hit the full-screen spinner, and stepping through a binder
        // blanked the header, the back button and the prev/next row each time.
        hasLoadedOnceRef.current = true;
      }
    };

    fetchCard();
  }, [cardId, user, activeProfileId, isTcgCard, activeProfile?.id]);

  // Fetch price from card_prices cache
  useEffect(() => {
    // A TCG Pocket card has no cached row and never will. Asking for one costs
    // a round trip and leaves the section waiting on an answer that is not
    // coming, so it is settled here instead.
    if (isPocketCard) { setPriceRow(null); setEvidenceReady(true); return; }
    if (!card?.name || !pricingEnabled) return;
    let cancelled = false;
    // The ladder must not paint a number before this lands. Two effects fetch
    // pricing independently: one gets the vendor's aggregates, this one gets
    // the individual sales the headline is actually built from. Whichever
    // returned first used to win the pixel, so a card would show the vendor's
    // 30 day median for a beat and then visibly correct itself to our average
    // of the last three. On Riolu that was $11 turning into $6.84.
    setEvidenceReady(false);
    const settle = () => { if (!cancelled) setEvidenceReady(true); };
    const loadPrice = async () => {
      // The variant is part of the key and was being left off here, while the
      // lookup after a fetch passed it. Rows are written under the card's real
      // finish - "holo", "reverse holofoil" - so a query for variant '' matched
      // nothing, every row was a miss, and card detail re-fetched from the
      // vendor on every visit no matter how warm the cache was.
      const row = await getCardPriceRow(
        card.name,
        card.set_name || '',
        card.set_number || card.card_id?.split('-').pop() || '',
        card.variant || '',
      );
      if (cancelled) return;
      if (row) {
        // Opening a card is the demand signal the refresh tiers run on.
        bumpPriceReads([row]);
        setPriceRow(row);
        settle();

        // And acting on that signal, which nothing did.
        //
        // The nightly job marks a row `refresh_due` - after three days for a
        // card people keep opening, fourteen for one read this month, thirty
        // otherwise - and until now the ONLY thing in the app that consumed the
        // flag was the bulk scanner. So a card refreshed when somebody scanned
        // and never when somebody looked at it. Mega Dragonite ex sat on sales
        // from the 1st while its read count climbed to eight, because reading
        // it recorded the demand and then ignored it.
        //
        // Deliberately not awaited: the cached row is already on screen and the
        // new one replaces it when it lands, so nobody waits on a vendor for a
        // price they can already see.
        if (row.refresh_due && !isRefreshLocked(row)) {
          fetchAndCacheSingleCard({
            name: card.name,
            setName: card.set_name || '',
            setNumber: card.set_number || card.card_id?.split('-').pop() || '',
            variant: card.variant || '',
            grade: card.grade || 'NM',
          })
            // It writes the row and returns nothing, so the fresh one is read
            // back rather than taken from the call.
            .then(() => getCardPriceRow(
              card.name,
              card.set_name || '',
              card.set_number || card.card_id?.split('-').pop() || '',
              card.variant || '',
            ))
            .then((fresh) => {
              if (cancelled || !fresh) return;
              setPriceRow(fresh);
              const next = getPriceForCard(
                fresh.stats, card.grade || 'NM', card.set_name, null, fresh.evidence, heldOut, fresh.asks,
              );
              if (next != null) setCardPrice({ price: next });
              setPriceDetailsInfo(
                getPriceDetails(fresh.stats, card.grade || 'NM', card.set_name, null, fresh.evidence),
              );
            })
            .catch((err) => console.warn('[price] refresh failed', err?.message));
        }
        // Paint the ladder from the cache now rather than waiting on the
        // vendor. This row is one indexed read and usually carries the same
        // aggregates; the vendor result replaces it the moment it lands. On a
        // card the vendor has to search for from scratch this is the difference
        // between a number in under a second and a number in thirty.
        if (!vendorTiersRef.current) {
          const seeded = tiersFromStats(row.stats);
          if (seeded) setPriceTiers((prev) => (vendorTiersRef.current ? prev : seeded));
        }
        // The TCGplayer column, from the same row, in the same read. It used to
        // arrive two live vendor calls later on every single visit, which is
        // why eBay was instant and TCGplayer always asked for a moment.
        // What we already hold goes up at once, whatever its age. Waiting on a
        // live vendor call before showing a number we have is why TCGplayer
        // lagged behind eBay on every visit. A stale figure is still refreshed
        // behind it: rawSourcesRef stays false, so the fetch above runs and
        // replaces this the moment it answers.
        if (row.asks && Object.keys(row.asks).length) {
          setPriceSources({ justTCG: { conditions: row.asks } });
          if (asksAreFresh(row.asks_at)) rawSourcesRef.current = true;
        }
        const price = getPriceForCard(row.stats, card.grade || 'NM', card.set_name, null, row.evidence, heldOut, row.asks);
        if (price != null) {
          setCardPrice({ price });
        }
        setPriceDetailsInfo(getPriceDetails(row.stats, card.grade || 'NM', card.set_name, null, row.evidence));
      } else {
        // No cached row, so the vendor has to be asked. Wait for the lookup the
        // ladder already started rather than starting a second one: this used
        // to double the work on exactly the cards that were slowest, the ones
        // nobody had priced before.
        const shared = tiersPromiseRef.current
          ? await tiersPromiseRef.current.catch(() => null)
          : null;
        if (cancelled) return;

        fetchAndCacheSingleCard({
          tiers: shared,
          name: card.name,
          setName: card.set_name || '',
          setNumber: card.set_number || card.card_id?.split('-').pop() || '',
          variant: card.variant || '',
          grade: card.grade || 'NM',
        }).then(async () => {
          if (cancelled) return;
          const newRow = await getCardPriceRow(
            card.name,
            card.set_name || '',
            card.set_number || card.card_id?.split('-').pop() || '',
            card.variant || '',
          );
          if (!cancelled && newRow) {
            setPriceRow(newRow);
            const price = getPriceForCard(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence, heldOut, newRow.asks);
            if (price != null) setCardPrice({ price });
            setPriceDetailsInfo(getPriceDetails(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence));
          }
          // Settled either way. A card with nothing to cache still has to stop
          // the ladder waiting, or it shimmers forever.
          settle();
        }).catch(settle);
      }
    };
    loadPrice().catch(settle);
    return () => { cancelled = true; };
  }, [card?.name, card?.set_number, card?.set_name, card?.variant, card?.grade, card?.card_id, pricingEnabled, isPocketCard]);

  // Ensure currentUserId, wishListCount, and isWished are set when activeProfile becomes available
  // This handles cases where the profile loads after the card data
  useEffect(() => {
    if (activeProfile?.id && !currentUserId) {
      setCurrentUserId(activeProfile.id);
      // Fetch wish list data if not already loaded
      const fetchWishListData = async () => {
        // Build tcgdex card ID for checking if wished
        let tcgdexCardId = null;
        if (card?.tcg_set_id && card?.tcg_local_id) {
          tcgdexCardId = `${card.tcg_set_id}-${card.tcg_local_id}`;
        } else if (card?.card_id) {
          tcgdexCardId = card.card_id;
        }

        const { data, count } = await supabase
          .from('wish_list')
          .select('tcgdex_card_id', { count: 'exact' })
          .eq('user_id', activeProfile.id)
          .eq('is_priority', true);

        setWishListCount(count || 0);
        if (tcgdexCardId) {
          setIsWished(data?.some(w => w.tcgdex_card_id === tcgdexCardId) || false);
        }
      };
      fetchWishListData();
    }
  }, [activeProfile?.id, currentUserId, card]);

  // Wish list functions - works for both TCGdex cards and regular database cards
  const handleAddToWishList = async () => {
    if (requireAuth('save to your wish list')) return;
    // A TCG Pocket card is free inside the Pocket app, so there is nothing to
    // wish for. The button is not rendered for these, so this is the backstop
    // that keeps a future caller from writing a row the list should not hold.
    if (isPocketCard) return;
    // Use activeProfile.id as fallback if currentUserId not yet set
    const userId = currentUserId || activeProfile?.id;
    if (!userId || isTopTenFull(wishListCount)) {
      console.log('[WishList] Cannot add - userId:', userId, 'topTenCount:', wishListCount);
      return;
    }

    // Determine card info based on card type
    let tcgdexCardId, setId, localId, cardName, setName, imageUrl;

    console.log('[WishList] Card type check:', { tcg_set_id: card?.tcg_set_id, card_id: card?.card_id, card });

    if (card?.tcg_set_id) {
      // TCGdex card (from wish list browser)
      setId = card.tcg_set_id;
      localId = card.tcg_local_id;
      tcgdexCardId = `${setId}-${localId}`;
      cardName = card.name;
      setName = card.set_name;
      imageUrl = card.digital_image_url?.replace('/high.webp', '') || null;
    } else if (card?.card_id) {
      // Regular database card - extract set_id and local_id from card_id
      // card_id format: "{setId}-{localId}" e.g., "base1-25"
      const parts = card.card_id.split('-');
      localId = parts.pop(); // Last part is localId
      setId = parts.join('-'); // Rest is setId (handles set IDs with dashes)
      tcgdexCardId = card.card_id;
      cardName = card.name;
      setName = card.set_name || 'Unknown Set';
      // For regular cards, we need to fetch the image URL from TCGdex
      imageUrl = null; // Will fetch below
    } else {
      return; // Can't add to wish list without card info
    }

    setAddingToWishList(true);
    try {
      // If no image URL, try to fetch from TCGdex
      if (!imageUrl && setId && localId) {
        try {
          const res = await fetch(`https://api.tcgdex.net/v2/en/cards/${setId}-${localId}`);
          if (res.ok) {
            const tcgData = await res.json();
            imageUrl = tcgData.image || null;
            if (!setName || setName === 'Unknown Set') {
              setName = tcgData.set?.name || setName;
            }
          }
        } catch (e) {
          console.log('Could not fetch TCGdex data for image');
        }
      }

      console.log('[WishList] Inserting:', { userId, tcgdexCardId, cardName, setId, setName });
      const { error } = await supabase
        .from('wish_list')
        .insert({
          user_id: userId,
          tcgdex_card_id: tcgdexCardId,
          card_name: cardName,
          set_id: setId,
          set_name: setName,
          variant: 'Normal',
          image_url: imageUrl,
          // Every card added from this screen goes into the ranked ten, which
          // is the only list left. The cap is checked above and enforced by the
          // trigger; the old version compared the whole wish list against
          // twenty and then wrote this flag anyway, so a full grid threw.
          is_priority: true,
        });

      if (error) {
        if (isTopTenCapError(error)) {
          console.log('[WishList] Top 10 is full');
          return;
        }
        console.error('[WishList] Insert error:', error);
        throw error;
      }
      setIsWished(true);
      setWishListCount(prev => prev + 1);
    } catch (e) {
      console.error('Error adding to wish list:', e);
    } finally {
      setAddingToWishList(false);
    }
  };

  const handleRemoveFromWishList = async () => {
    // Use activeProfile.id as fallback if currentUserId not yet set
    const userId = currentUserId || activeProfile?.id;
    if (!userId) return;

    // Determine tcgdex_card_id based on card type
    let tcgdexCardId;
    if (card?.tcg_set_id) {
      tcgdexCardId = `${card.tcg_set_id}-${card.tcg_local_id}`;
    } else if (card?.card_id) {
      tcgdexCardId = card.card_id;
    } else {
      return;
    }

    setRemovingFromWishList(true);
    try {
      const { error } = await supabase
        .from('wish_list')
        .delete()
        .eq('user_id', userId)
        .eq('tcgdex_card_id', tcgdexCardId);

      if (error) throw error;
      setIsWished(false);
      setWishListCount(prev => prev - 1);
    } catch (e) {
      console.error('Error removing from wish list:', e);
    } finally {
      setRemovingFromWishList(false);
    }
  };

  // Handle URL params to open photo viewer
  useEffect(() => {
    if (!card || initialViewHandled || loading) return;

    if (viewParam) {
      // Build photos array inline
      const photos = [];
      if (card.digital_image_url) photos.push({ label: 'Digital' });
      if (card.original_image_url) photos.push({ label: 'Photo' });
      if (card.back_photo) photos.push({ label: 'Back' });
      if (card.front_top_photo) photos.push({ label: 'Front Top' });
      if (card.front_bottom_photo) photos.push({ label: 'Front Bottom' });
      if (card.back_top_photo) photos.push({ label: 'Back Top' });
      if (card.back_bottom_photo) photos.push({ label: 'Back Bottom' });

      setCameFromExternal(true); // Mark that we came here via URL params

      if (viewParam === 'grid') {
        setPhotoViewMode('grid');
        setPhotoViewerOpen(true);
      } else if (viewParam === 'zoom') {
        setPhotoViewMode('zoom');

        if (photoParam === 'back') {
          // Find the back photo index
          const backIndex = photos.findIndex(p => p.label === 'Back');
          setSelectedPhotoIndex(backIndex >= 0 ? backIndex : 0);
        } else if (photoParam) {
          const idx = parseInt(photoParam, 10);
          setSelectedPhotoIndex(isNaN(idx) ? 0 : idx);
        }

        setPhotoViewerOpen(true);
      }

      setInitialViewHandled(true);
    }
  }, [card, viewParam, photoParam, loading, initialViewHandled]);

  const handleBack = () => {
    // From a deal, back is exactly one hop, to the deal. The prev/next
    // counter below belongs to whatever list was browsed before and is kept
    // for half an hour; honouring it here rewound past the scanner entirely.
    if (dealMode) {
      navigate(-1);
      return;
    }

    // Arrived on a link, so there is nothing behind this page: going "back"
    // would leave the site. The binder they are looking at a card from is the
    // right place instead.
    if (linkBinderId && navInfo.contextType === 'binder') {
      const owner = cardOwner?.id || card?.user_id;
      clearCardNavigationContext();
      navigate(owner
        ? `/user/${owner}/collection?tab=binders&binder=${linkBinderId}`
        : '/', { replace: true });
      return;
    }

    // Get how many card navigations we've done and go back past all of them
    const navCount = getNavCount();
    clearCardNavigationContext();

    if (navCount > 0) {
      // Go back past all the card-to-card navigations plus the initial card view
      window.history.go(-(navCount + 1));
    } else {
      navigate(-1);
    }
  };

  // Lazy load user cards and open post modal
  const handleOpenPostModal = async () => {
    // Only fetch if we don't already have the cards
    if (userCards.length === 0 && currentUserId) {
      const { data: allCardsData } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', currentUserId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (allCardsData) {
        setUserCards(allCardsData);
      }
    }
    setPostModalOpen(true);
  };

  // Close photo viewer - go back if came from Home, otherwise just close
  const handleClosePhotoViewer = () => {
    if (cameFromExternal) {
      navigate(-1); // Go back to where we came from (Home)
    } else {
      setPhotoViewerOpen(false);
    }
  };

  // Toggle follow card owner
  const handleToggleFollow = async () => {
    if (requireAuth('follow this trader')) return;
    if (!currentUserId || !card?.user_id) return;

    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    try {
      if (wasFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', card.user_id);
      } else {
        await supabase
          .from('user_follows')
          .insert({
            follower_id: currentUserId,
            following_id: card.user_id,
          });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      setIsFollowing(wasFollowing);
    }
  };

  // Block card owner
  const handleBlockUser = async () => {
    if (requireAuth('block a user')) return;
    if (!currentUserId || !card?.user_id) return;

    try {
      const { error } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: currentUserId,
          blocked_id: card.user_id,
        });

      if (error) {
        if (error.code === '23505') {
          alert('User is already blocked');
        } else {
          throw error;
        }
      } else {
        alert('User blocked. You will no longer see their content.');
        navigate(-1);
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  // Report card
  const handleReportCard = () => {
    if (requireAuth('report a card')) return;
    setReportModalOpen(true);
  };

  // Handle successful report
  const handleReportSuccess = () => {
    setReportSuccessOpen(true);
  };

  // Binder management
  const openBinderPicker = () => {
    setSelectedBinder(cardBinder || null);
    setPreviewBinder(null);
    setPreviewCards([]);
    setBinderPickerOpen(true);
  };

  const handleQuickView = async (binder) => {
    if (previewBinder?.id === binder.id) {
      // Toggle off
      setPreviewBinder(null);
      setPreviewCards([]);
      return;
    }

    setPreviewBinder(binder);

    try {
      const { data } = await supabase
        .from('cards')
        .select('id, digital_image_url, name')
        .eq('binder_id', binder.id)
        .is('deleted_at', null)
        .limit(9);

      setPreviewCards(data || []);
    } catch (err) {
      console.error('Error fetching preview:', err);
      setPreviewCards([]);
    }
  };

  const handleBinderDone = async () => {
    setBinderPickerOpen(false);

    if (!selectedBinder && cardBinder) {
      // A tradable card cannot go loose. Say so rather than silently keeping
      // it where it is, which reads as the tap not registering.
      if (card?.trading_status === 'TRADE') {
        setBinderRuleNote(TRADE_NEEDS_BINDER_WARNING);
        setSelectedBinder(null);
        setPreviewBinder(null);
        setPreviewCards([]);
        return;
      }
      // Removing from binder
      const oldBinder = cardBinder;
      setCardBinder(null);
      setCard(prev => ({ ...prev, binder_id: null }));

      try {
        await supabase.from('card_binders').delete()
          .eq('card_id', cardId).eq('binder_id', oldBinder.id);
        await supabase.from('cards').update({ binder_id: null }).eq('id', cardId);
      } catch (err) {
        console.error('Error removing from binder:', err);
      }
    } else if (selectedBinder && selectedBinder.id !== cardBinder?.id) {
      // Adding or changing binder
      const oldBinder = cardBinder;
      setCardBinder(selectedBinder);
      setCard(prev => ({ ...prev, binder_id: selectedBinder.id }));

      try {
        if (oldBinder) {
          await supabase.from('card_binders').delete()
            .eq('card_id', cardId).eq('binder_id', oldBinder.id);
        }
        await supabase.from('card_binders').insert({ card_id: cardId, binder_id: selectedBinder.id });
        await supabase.from('cards').update({ binder_id: selectedBinder.id }).eq('id', cardId);
      } catch (err) {
        console.error('Error updating binder:', err);
      }
    }

    setSelectedBinder(null);
    setPreviewBinder(null);
    setPreviewCards([]);
  };

  const handleRemoveFromBinder = async () => {
    if (!cardBinder) return;
    // Same rule as the picker: tradable stays in a binder.
    if (card?.trading_status === 'TRADE') {
      setBinderRuleNote(TRADE_NEEDS_BINDER_WARNING);
      return;
    }

    const oldBinder = cardBinder;
    setCardBinder(null);
    setCard(prev => ({ ...prev, binder_id: null }));

    try {
      await supabase.from('card_binders').delete()
        .eq('card_id', cardId).eq('binder_id', oldBinder.id);
      await supabase.from('cards').update({ binder_id: null }).eq('id', cardId);
    } catch (err) {
      console.error('Error removing from binder:', err);
    }
  };

  // Grade/Condition options
  const rawConditions = [
    { value: 'NM', label: 'Near Mint (NM)' },
    { value: 'LP', label: 'Lightly Played (LP)' },
    { value: 'MP', label: 'Moderately Played (MP)' },
    { value: 'HP', label: 'Heavily Played (HP)' },
    { value: 'DMG', label: 'Damaged (DMG)' },
  ];

  const psaGrades = [
    { value: 'PSA 10', label: 'PSA 10 - Gem Mint' },
    { value: 'PSA 9', label: 'PSA 9 - Mint' },
    { value: 'PSA 8', label: 'PSA 8 - NM-MT' },
    { value: 'PSA 7', label: 'PSA 7 - NM' },
    { value: 'PSA 6', label: 'PSA 6 - EX-MT' },
    { value: 'PSA 5', label: 'PSA 5 - EX' },
  ];

  const cgcGrades = [
    { value: 'CGC 10', label: 'CGC 10 - Pristine' },
    { value: 'CGC 9.5', label: 'CGC 9.5 - Gem Mint' },
    { value: 'CGC 9', label: 'CGC 9 - Mint' },
    { value: 'CGC 8.5', label: 'CGC 8.5 - NM-MT+' },
    { value: 'CGC 8', label: 'CGC 8 - NM-MT' },
  ];

  const bgsGrades = [
    { value: 'BGS 10', label: 'BGS 10 - Pristine' },
    { value: 'BGS 9.5', label: 'BGS 9.5 - Gem Mint' },
    { value: 'BGS 9', label: 'BGS 9 - Mint' },
    { value: 'BGS 8.5', label: 'BGS 8.5 - NM-MT+' },
    { value: 'BGS 8', label: 'BGS 8 - NM-MT' },
  ];

  const aceGrades = [
    { value: 'ACE 10', label: 'ACE 10 - Gem Mint' },
    { value: 'ACE 9', label: 'ACE 9 - Mint' },
    { value: 'ACE 8', label: 'ACE 8 - NM-MT' },
    { value: 'ACE 7', label: 'ACE 7 - NM' },
    { value: 'ACE 6', label: 'ACE 6 - EX-MT' },
  ];

  // TAG issues half grades and Poketrace prices them (TAG 6.5, 7.5, 8.5 all
  // return real sales), so offering only whole numbers meant a user could not
  // record what is actually on their slab.
  const tagGrades = [
    { value: 'TAG 10', label: 'TAG 10 - Gem Mint' },
    { value: 'TAG 9.5', label: 'TAG 9.5' },
    { value: 'TAG 9', label: 'TAG 9 - Mint' },
    { value: 'TAG 8.5', label: 'TAG 8.5' },
    { value: 'TAG 8', label: 'TAG 8 - NM-MT' },
    { value: 'TAG 7.5', label: 'TAG 7.5' },
    { value: 'TAG 7', label: 'TAG 7 - NM' },
    { value: 'TAG 6.5', label: 'TAG 6.5' },
    { value: 'TAG 6', label: 'TAG 6 - EX-MT' },
    { value: 'TAG 5', label: 'TAG 5 - EX' },
    { value: 'TAG 4', label: 'TAG 4 - VG-EX' },
    { value: 'TAG 3', label: 'TAG 3 - VG' },
    { value: 'TAG 2', label: 'TAG 2 - Good' },
    { value: 'TAG 1', label: 'TAG 1 - Poor' },
  ];

  // Generic grades for "Other" custom company
  const otherGrades = [
    { value: '10', label: '10 - Gem Mint' },
    { value: '9.5', label: '9.5 - Gem Mint' },
    { value: '9', label: '9 - Mint' },
    { value: '8.5', label: '8.5 - NM-MT+' },
    { value: '8', label: '8 - NM-MT' },
    { value: '7', label: '7 - NM' },
    { value: '6', label: '6 - EX-MT' },
  ];

  const getGradeOptions = () => {
    if (gradeType === 'raw') return rawConditions;
    if (gradingCompany === 'PSA') return psaGrades;
    if (gradingCompany === 'CGC') return cgcGrades;
    if (gradingCompany === 'BGS') return bgsGrades;
    if (gradingCompany === 'ACE') return aceGrades;
    if (gradingCompany === 'TAG') return tagGrades;
    if (gradingCompany === 'Other') return otherGrades;
    return bgsGrades;
  };

  const openGradePicker = () => {
    setSelectedGrade(card?.grade || 'NM');
    setGradePickerOpen(true);
  };

  const handleGradeConfirm = async () => {
    if (!cardId || savingGrade || !selectedGrade) return;

    // For "Other" company, prepend custom company name to grade
    let finalGrade = selectedGrade;
    if (gradingCompany === 'Other' && customCompany.trim()) {
      finalGrade = `${customCompany.trim()} ${selectedGrade}`;
    }

    if (finalGrade === card?.grade) {
      setGradePickerOpen(false);
      return;
    }

    setSavingGrade(true);
    const oldGrade = card.grade;

    // Optimistic update
    setCard(prev => ({ ...prev, grade: finalGrade }));
    setGradePickerOpen(false);

    try {
      const { error } = await supabase
        .from('cards')
        .update({ grade: finalGrade, updated_at: new Date().toISOString() })
        .eq('id', cardId);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating grade:', err);
      // Revert on error
      setCard(prev => ({ ...prev, grade: oldGrade }));
    } finally {
      setSavingGrade(false);
    }
  };

  // Variant picker functions
  /**
   * Which printings this card actually has, from OUR catalogue.
   *
   * This used to fetch api.tcgdex.net directly and build a flat list that mixed
   * a finish (Holo) with a printing (1st Edition) with a print type (Cosmos
   * Holo). Two things wrong with that: tcgdex started 404ing months ago and the
   * app already self-hosts everything it needs, and the flat list meant picking
   * "1st Edition" wrote a LABEL and nothing else - so a card marked 1st Edition
   * here still carried no v_stamps, still showed no badge, and still did not
   * count toward a 1st Edition master set.
   *
   * The owner's shape: "options that the card has for sure, then the extra
   * notes almost like error card or halo bleed is a different pill." So this is
   * the first half - the axes the catalogue says this card genuinely has.
   */
  const openVariantPicker = async () => {
    setVariantDraft({
      type: card?.v_type || null,
      subtype: card?.v_subtype || null,
      stamps: Array.isArray(card?.v_stamps) ? card.v_stamps.filter(Boolean) : [],
    });
    setVariantAxes(null);
    try {
      setVariantAxes(await getVariantOptions(card?.card_id));
    } catch (err) {
      console.warn('[CardDetail] could not read the printings', err?.message);
      setVariantAxes({ types: [], foilsByType: {}, subtypes: [], stamps: [], count: 0 });
    }
  };

  const handleVariantConfirm = async () => {
    if (!cardId || savingVariant) return;
    setSavingVariant(true);

    const before = {
      variant: card?.variant,
      v_type: card?.v_type,
      v_subtype: card?.v_subtype,
      v_stamps: card?.v_stamps,
    };

    // All four columns, not just the label.
    //
    // The label is what a person reads; v_type, v_subtype and v_stamps are what
    // the badge, the master set progress and the price lookup all read. Writing
    // only the label is why a card marked 1st Edition here stayed unbadged and
    // never counted toward a 1st Edition master set.
    const stamps = [...(variantDraft.stamps || [])].filter(Boolean).sort();
    const next = {
      v_type: variantDraft.type || null,
      v_subtype: variantDraft.subtype || null,
      v_stamps: stamps.length ? stamps : null,
      variant: variantLabel({
        type: variantDraft.type,
        subtype: variantDraft.subtype,
        stamps,
      }) || 'Normal',
      updated_at: new Date().toISOString(),
    };

    setCard((prev) => ({ ...prev, ...next }));
    setVariantPickerOpen(false);

    try {
      const { error } = await supabase.from('cards').update(next).eq('id', cardId);
      if (error) throw error;
    } catch (err) {
      console.error('Error updating variant:', err);
      setCard((prev) => ({ ...prev, ...before }));
    } finally {
      setSavingVariant(false);
    }
  };

  // Remove post (timer stays - user can't repost until cooldown ends)
  const handleRemovePost = async () => {
    if (!currentUserId || !cardId) return;

    try {
      // Find the most recent post containing this card by this user
      const { data: postData } = await supabase
        .from('posts_cards')
        .select('post_id, posts!posts_cards_post_id_fkey(id, user_id)')
        .eq('card_id', cardId);

      if (postData && postData.length > 0) {
        // Find posts by current user
        const userPosts = postData.filter(p => p.posts?.user_id === currentUserId);

        for (const post of userPosts) {
          // Delete from posts_cards junction table
          await supabase
            .from('posts_cards')
            .delete()
            .eq('post_id', post.post_id)
            .eq('card_id', cardId);

          // Check if this post has any other cards
          const { data: remainingCards } = await supabase
            .from('posts_cards')
            .select('id')
            .eq('post_id', post.post_id);

          // If no cards left, delete the post itself
          if (!remainingCards || remainingCards.length === 0) {
            await supabase
              .from('posts')
              .delete()
              .eq('id', post.post_id);
          }
        }
      }

      // Note: Timer (daysUntilRepost) stays as-is - we don't reset it
      // This prevents users from gaming the system by deleting and reposting
    } catch (err) {
      console.error('Error removing post:', err);
    }
  };

  // Toggle trading status between TRADE and PC
  const handleToggleTradingStatus = async () => {
    if (!cardId) return;
    // A TCG Pocket card can never be marked tradable. Trade Now is two people
    // at a table swapping printed cards; this one only exists in the Pocket
    // app. The chip below is not tappable for these, so this is the backstop.
    if (isPocketCard) return;

    const newStatus = card.trading_status === 'TRADE' ? 'PC' : 'TRADE';

    // There is no binder to choose. Everything tradable lives in the trade
    // binder, and `cc_trade_binder_guard` moves the card there as part of the
    // write, so the card comes back already filed. The note below the chip
    // says so, because the card physically leaves wherever it was.
    const previousBinderId = card.binder_id;

    setCard(prev => ({ ...prev, trading_status: newStatus }));
    setBinderRuleNote(newStatus === 'TRADE' ? TRADE_BINDER_MOVE_NOTE : '');

    try {
      const { data, error } = await supabase
        .from('cards')
        .update({ trading_status: newStatus })
        .eq('id', cardId)
        .select('id, binder_id, trading_status')
        .single();
      if (error) throw error;

      // The binder the database picked, not the one we guessed.
      if (data && data.binder_id !== previousBinderId) {
        setCard(prev => ({ ...prev, binder_id: data.binder_id }));
        if (data.binder_id) {
          const { data: b } = await supabase
            .from('binders')
            .select('id, name, image, is_trade_binder')
            .eq('id', data.binder_id)
            .maybeSingle();
          setCardBinder(b || null);
        } else {
          setCardBinder(null);
        }
      }
    } catch (err) {
      console.error('Error updating trading status:', err);
      setCard(prev => ({ ...prev, trading_status: card.trading_status }));
      setBinderRuleNote('');
    }
  };

  // Open trade confirmation modal with nearest location lookup
  const handleTradeButtonClick = async () => {
    if (requireAuth('start a chat')) return;
    // Nothing digital crosses a table. Unreachable while a Pocket card cannot
    // be marked tradable, kept so it stays unreachable if that ever slips.
    if (isPocketCard) return;
    // Viewer-only restriction: Block trading for minors without parent permission
    if (isViewerOnly) {
      setShowParentModal(true);
      return;
    }

    if (!currentUserId || !card || sendingRequest || tradeRequestSent) return;

    // The card is a door. What opens is a question about the person, so the
    // sheet fetches their trade binder rather than anything about this card.
    setTradeConfirmOpen(true);
    setOwnerTradeBinder(null);

    try {
      const { data: tb } = await supabase
        .from('binders')
        .select('id, name, is_trade_binder')
        .eq('user_id', card.user_id)
        .eq('is_trade_binder', true)
        .is('deleted_at', null)
        .order('created_at')
        .limit(1)
        .maybeSingle();
      if (tb) {
        const { count } = await supabase
          .from('cards')
          .select('id', { count: 'exact', head: true })
          .eq('binder_id', tb.id)
          .is('deleted_at', null);
        setOwnerTradeBinder({ ...tb, count: count ?? 0 });
      }
    } catch (err) {
      // No binder to offer is not a reason to block the ask.
      console.log('Trade binder lookup failed:', err);
    }

    // Where to meet is flow 2's question, asked in the chat by Chase once
    // both people have agreed to meet at all. Naming a shop here decided
    // something nobody had agreed to yet.
  };

  // Execute trade request after confirmation
  const executeSendTradeRequest = async () => {
    if (!currentUserId || !card || sendingRequest || tradeRequestSent) return;

    setTradeConfirmOpen(false);
    setSendingRequest(true);
    try {
      // One request between two people, whatever card got them here. The card
      // rides along as provenance and is never read back as what was wanted.
      const { error, alreadyOpen } = await requestChat({
        requesterId: currentUserId,
        ownerId: card.user_id,
        fromCardId: cardId,
      });

      if (alreadyOpen) {
        setTradeRequestSent(true);
        return;
      }

      if (error) {
        console.error('Error starting chat:', error);
        alert('That did not send. Please try again.');
      } else {
        setTradeRequestSent(true);
        handleTradeRequestSent(); // Trigger first trade request tip if applicable

        // Send push notification to card owner
        try {
          const { data: requesterData } = await supabase
            .from('users')
            .select('username')
            .eq('id', currentUserId)
            .single();
          const requesterName = requesterData?.username || 'Someone';
          sendTradeRequestNotification(card.user_id, requesterName, card.name);
        } catch (notifErr) {
          console.log('Notification send failed:', notifErr);
        }
      }
    } catch (err) {
      console.error('Error sending trade request:', err);
      alert('Failed to send trade request. Please try again.');
    } finally {
      setSendingRequest(false);
    }
  };

  // Get all available photos for the card (user photos only, no digital)
  const getCardPhotos = () => {
    if (!card) return [];
    const photos = [];

    // Main photo (user's original photo, not digital)
    if (card.original_image_url) {
      photos.push({ url: card.original_image_url, label: 'Photo' });
    }
    // Additional photos
    if (card.back_photo) {
      photos.push({ url: card.back_photo, label: 'Back' });
    }
    if (card.front_top_photo) {
      photos.push({ url: card.front_top_photo, label: 'Front Top' });
    }
    if (card.front_bottom_photo) {
      photos.push({ url: card.front_bottom_photo, label: 'Front Bottom' });
    }
    if (card.back_top_photo) {
      photos.push({ url: card.back_top_photo, label: 'Back Top' });
    }
    if (card.back_bottom_photo) {
      photos.push({ url: card.back_bottom_photo, label: 'Back Bottom' });
    }
    if (card.other_photo_1) {
      photos.push({ url: card.other_photo_1, label: 'Other 1' });
    }
    if (card.other_photo_2) {
      photos.push({ url: card.other_photo_2, label: 'Other 2' });
    }

    // Digital image always last
    if (card.digital_image_url) {
      photos.push({ url: card.digital_image_url, label: 'Digital' });
    }

    return photos;
  };

  const cardPhotos = card ? getCardPhotos() : [];
  const hasMultiplePhotos = cardPhotos.length > 1;

  // Initialize styles with theme
  const componentStyles = styles(theme, isDark);

  // Arriving at card detail: full-screen spinner, as before.
  // Stepping to the next card: fall through, keep the header and the arrows on
  // screen, and let the body render its own skeletons.
  if (loading && !hasLoadedOnceRef.current) {
    return (
      <div style={componentStyles.loadingContainer}>
        <div style={componentStyles.spinner} />
      </div>
    );
  }
  if (loading && !card) {
    return (
      <div style={componentStyles.loadingContainer}>
        <div style={componentStyles.spinner} />
      </div>
    );
  }

  if (!card) {
    return (
      <div style={componentStyles.errorContainer}>
        <p style={componentStyles.errorText}>Card not found</p>
        <button style={componentStyles.backButton} onClick={handleBack}>
          Go Back
        </button>
      </div>
    );
  }

  // Card is hidden (under review by admin)
  if (card.isHidden) {
    return (
      <div style={componentStyles.errorContainer}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p style={{ ...componentStyles.errorText, marginBottom: 8 }}>Card Under Review</p>
        <p style={{ color: theme.textMuted, fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
          This card has been reported and is being reviewed by our team.
        </p>
        <button style={componentStyles.backButton} onClick={handleBack}>
          Go Back
        </button>
      </div>
    );
  }

  const imageUrl = card.digital_image_url || card.original_image_url;

  // Hide main content when coming from external and photo viewer is open
  const hideMainContent = cameFromExternal && photoViewerOpen;

  return (
    <div style={{
      ...componentStyles.container,
      ...(hideMainContent ? { backgroundColor: theme.bgPure } : {}),
    }}>
      {/* Main content */}
      <div style={hideMainContent ? { display: 'none' } : { height: '100%' }}>
      {/* Header */}
      <div style={componentStyles.header}>
        <button style={componentStyles.headerBackBtn} onClick={handleBack}>
          <BackIcon />
        </button>
        <div style={componentStyles.headerCenter}>
          {dealMode ? (
            <h1 style={componentStyles.headerTitle}>Reviewing</h1>
          ) : isOwnCard ? (
            <h1 style={componentStyles.headerTitle}>My Card</h1>
          ) : isTcgCard ? (
            /* A catalogue entry has no owner. The owner button fell through to
               its own placeholder and put "@trader - tap to view collection"
               above a card nobody has, leading nowhere. */
            <h1 style={componentStyles.headerTitle}>Card</h1>
          ) : (
            <button
              style={componentStyles.headerOwnerBtn}
              onClick={() => cardOwner?.id && navigate(`/user/${cardOwner.id}/collection`)}
            >
              <div style={componentStyles.headerOwnerAvatar}>
                {cardOwner?.avatar_url
                  ? <img src={cardOwner.avatar_url} alt={cardOwner.username} style={componentStyles.headerOwnerImg} />
                  : <span>{cardOwner?.username?.[0]?.toUpperCase() || '?'}</span>}
              </div>
              <div style={{ minWidth: 0 }}>
                <h1 style={componentStyles.headerTitle}>@{cardOwner?.username || 'trader'}</h1>
                <span style={componentStyles.headerOwnerSub}>tap to view collection</span>
              </div>
            </button>
          )}
        </div>
        <div style={componentStyles.menuWrapper}>
          {!dealMode && (
          <button style={componentStyles.headerMenuBtn} onClick={() => setMenuOpen(true)}>
            <MoreVerticalIcon />
          </button>
          )}
          <CardOptionsMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            isFollowing={isFollowing}
            onToggleFollow={handleToggleFollow}
            onReportCard={handleReportCard}
            onBlock={handleBlockUser}
            isOwnCard={isOwnCard}
            theme={theme}
          />
        </div>
      </div>

      {/* Card Navigation Bar - shows when navigating through a list */}
      {(navInfo.hasPrev || navInfo.hasNext) && (
        <div style={componentStyles.cardNavBar}>
          {navInfo.hasPrev ? (
            <button style={componentStyles.cardNavBtn} onClick={handleNavigatePrev}>
              <ChevronLeftIcon size={14} />
              <span style={componentStyles.cardNavText}>Prev</span>
            </button>
          ) : (
            <div style={componentStyles.cardNavPlaceholder} />
          )}

          <span style={componentStyles.cardNavCounter}>
            {navInfo.currentIndex + 1} of {navInfo.total}
            {getNavContextLabel() ? ` in ${getNavContextLabel()}` : ''}
          </span>

          {navInfo.hasNext ? (
            <button style={componentStyles.cardNavBtn} onClick={handleNavigateNext}>
              <span style={componentStyles.cardNavText}>Next</span>
              <ChevronRightIcon size={14} />
            </button>
          ) : (
            <div style={componentStyles.cardNavPlaceholder} />
          )}
        </div>
      )}

      {/* Content */}
      <div style={componentStyles.content}>
        {/* Top Section - Card + Info Side by Side */}
        <div style={componentStyles.topSection}>

          {/* Card Image */}
          <div
            style={componentStyles.cardImageWrapper}
            onClick={() => {
              setSelectedPhotoIndex(0);
              setPhotoViewerOpen(true);
            }}
          >
            <CardImage
              digitalImage={card.digital_image_url}
              userPhoto={card.original_image_url}
              name={card.name}
              number={card.number}
              setName={card.set_name}
              theme={theme}
              style={componentStyles.cardImage}
            />
            <div style={componentStyles.zoomHint}>
              <ZoomInIcon />
            </div>
          </div>

          {/* Quick Info */}
          <div style={componentStyles.quickInfo}>
            {browsingOnly ? (
              /* A catalogue card reads exactly like an owned one up here: name,
                 set and number, era, then a chip row. It used to be a labelled
                 table (Browsing / HP / Type / Rarity) beside a picture, so the
                 same card looked like two different apps depending on whether
                 you owned it. The owner: "the top part should all be the
                 same". Only the action card below differs, and it should. */
              <>
                <div style={componentStyles.factsTitleBtn}>
                  <span style={componentStyles.factsName}>{card.name}</span>
                </div>
                <div style={componentStyles.factsSet}>
                  {card.set_name}{card.set_number ? ` · #${card.set_number}` : ''}
                  {cardEra && <><br />{eraLine(cardEra)}</>}
                </div>
                <div style={componentStyles.chipRow}>
                  {/* The status names what this screen is for. For an ordinary
                      catalogue card that is the wish list; a Pocket card can
                      never go on a list, so it says what the card is instead. */}
                  <span style={{
                    ...componentStyles.statusBadge,
                    backgroundColor: isPocketCard ? theme.purpleBg : 'rgba(250, 204, 21, 0.15)',
                    color: isPocketCard ? theme.purple : '#facc15',
                  }}>
                    {isPocketCard ? TCG_POCKET_LABEL : 'Wish List'}
                  </span>
                  {/* Which printing was tapped to get here. Without it a
                      Shadowless tile and an Unlimited tile open the same
                      screen and neither says which one you are looking at. */}
                  {viewingPrinting && (
                    <span style={{ ...componentStyles.factChip, ...(chipTone(viewingPrinting) || { color: theme.text }) }}>
                      {viewingPrinting}
                    </span>
                  )}
                  {card.rarity && (
                    <span style={{ ...componentStyles.factChip, color: theme.text }}>{card.rarity}</span>
                  )}
                  {card.hp && (
                    <span style={{ ...componentStyles.factChip, color: theme.textSecondary }}>{card.hp} HP</span>
                  )}
                  {card.types && card.types.length > 0 && (
                    <span style={{ ...componentStyles.factChip, color: theme.textSecondary }}>{card.types.join(' · ')}</span>
                  )}
                </div>
              </>
            ) : (
              /* Regular card info */
              <>
                {/* Card identity leads the facts, since the header now carries the owner */}
                <button
                  style={componentStyles.factsTitleBtn}
                  onClick={canEdit ? () => { haptics.tap(); setNamePickerOpen(true); } : undefined}
                  disabled={!canEdit}
                >
                  <span style={componentStyles.factsName}>{card.name}</span>
                  {canEdit && (
                    <ChevronDownIcon style={{ width: 13, height: 13, flexShrink: 0, opacity: 0.75 }} />
                  )}
                </button>
                <div style={componentStyles.factsSet}>
                  {card.set_name}{card.set_number ? ` · #${card.set_number}` : ''}
                  {/* Era used to own a whole labelled row further down. It is a
                      fact about the card, not a setting, so it belongs in the
                      subtitle with the rest of the identity. */}
                  {cardEra && <><br />{eraLine(cardEra)}</>}
                </div>

                {/* Condition, finish and status as one chip row. Two of them
                    open the pickers they always did; status is display only
                    here because it is editable in the action card below. */}
                {/* Every editable fact about this copy, as one row.
                    Status used to be a display pill here AND a row in the card
                    below, which is two controls for one value; whichever you
                    did not use looked stale. Binder was a labelled row for the
                    same reason. Both are chips now and the card below is just
                    the post button. */}
                <div style={componentStyles.chipRow}>
                  <button
                    style={{ ...componentStyles.factChip, color: theme.text }}
                    onClick={canEdit ? openGradePicker : undefined}
                    disabled={!canEdit}
                  >
                    {card.grade || 'NM'}
                    {canEdit && (
                      <ChevronDownIcon style={{ width: 12, height: 12, color: theme.textSecondary }} />
                    )}
                  </button>

                  {/* "Holo / Shadowless / 1st Edition Stamp" is three facts the
                      scanner recorded separately and joined for storage. One
                      chip that long wraps and reads as a sentence; split back
                      apart it reads as what it is. The finish leads in holo
                      yellow, the rest are printing details. */}
                  {/* No printing recorded, and no way to say so.
                      The owner: "I forgot to mark 1st ed. But I cant do that
                      from here." The finish chip opened this picker, but a card
                      whose finish is also blank had no chip at all and no door.
                      An explicit one, only on your own card. */}
                  {card.specification && (
                    <button
                      style={{ ...componentStyles.factChip, ...(chipTone(card.specification) || { color: theme.text }) }}
                      onClick={canEdit ? () => { haptics.select(); openVariantPicker(); } : undefined}
                      disabled={!canEdit}
                    >
                      {card.specification}
                      {canEdit && <ChevronDownIcon style={{ width: 12, height: 12, color: 'currentColor', opacity: 0.7 }} />}
                    </button>
                  )}

                  {canEdit && variantParts.length === 0 && !card.specification && (
                    <button
                      style={{ ...componentStyles.factChip, color: theme.textSecondary }}
                      onClick={() => { haptics.select(); openVariantPicker(); }}
                    >
                      Set printing
                      <ChevronDownIcon style={{ width: 12, height: 12, color: theme.textSecondary }} />
                    </button>
                  )}

                  {variantParts.map((part, i) => (
                    <button
                      key={part}
                      style={{
                        ...componentStyles.factChip,
                        color: i === 0 ? '#f5c542' : theme.text,
                        ...(i === 0 ? {} : (chipTone(part) || {})),
                      }}
                      onClick={canEdit ? () => { haptics.select(); openVariantPicker(); } : undefined}
                      disabled={!canEdit}
                    >
                      {part}
                      {canEdit && i === 0 && (
                        <ChevronDownIcon style={{ width: 12, height: 12, color: theme.textSecondary }} />
                      )}
                    </button>
                  ))}

                  {/* A TCG Pocket card has no trade state to toggle, so it
                      gets a chip that states the fact instead of a control
                      that cannot move. The reason sits under the row rather
                      than in the chip, where it would never fit. */}
                  {isOwnCard && isPocketCard && (
                  <span
                    style={{
                      ...componentStyles.factChip,
                      border: `0.5px solid ${theme.purple}`,
                      background: theme.purpleBg,
                      color: theme.purple,
                      cursor: 'default',
                    }}
                  >
                    {TCG_POCKET_TRADE_SHORT}
                  </span>
                  )}

                  {isOwnCard && !isPocketCard && (
                  <button
                    style={{
                      ...componentStyles.factChip,
                      border: '0.5px solid transparent',
                      ...(card.trading_status === 'TRADE'
                        ? { background: 'rgba(74, 222, 128, 0.14)', color: '#4ade80' }
                        : { background: 'rgba(167, 139, 250, 0.14)', color: '#a78bfa' }),
                    }}
                    onClick={canEdit ? () => { setBinderRuleNote(''); handleToggleTradingStatus(); } : undefined}
                    disabled={!canEdit}
                  >
                    {card.trading_status === 'TRADE' ? 'For Trade' : 'PC'}
                  </button>
                  )}

                  {/* The rule is not obvious from the chip, and the first time
                      someone meets it is the moment a tap asks them for a
                      binder. This is the answer to "why", one tap away and
                      never in the way. */}
                  {isOwnCard && !isPocketCard && canEdit && (
                  <button
                    style={{
                      ...componentStyles.factChip,
                      color: theme.textSecondary,
                      paddingLeft: '8px',
                      paddingRight: '8px',
                    }}
                    onClick={() => { setBinderRuleNote(''); setShowTradeBinderInfo((v) => !v); }}
                    aria-label="Why a tradable card needs a binder"
                    aria-expanded={showTradeBinderInfo}
                  >
                    <InfoIcon style={{ width: 12, height: 12 }} />
                  </button>
                  )}

                  {canEdit && (
                    <button
                      style={{
                        ...componentStyles.factChip,
                        color: isTradeBinder(cardBinder) ? '#4ade80'
                          : cardBinder ? theme.text : theme.textSecondary,
                      }}
                      onClick={openBinderPicker}
                    >
                      {cardBinder ? cardBinder.name : 'No binder'}
                      <ChevronDownIcon style={{ width: 12, height: 12, color: theme.textSecondary }} />
                    </button>
                  )}
                </div>

                {/* Why that chip does not move. One line, under the row, so
                    the chip stays chip-sized and the reason is still on the
                    screen that took the control away. */}
                {isOwnCard && isPocketCard && (
                  <div style={{ fontSize: '11.5px', lineHeight: 1.4, color: theme.textMuted, marginTop: '-2px' }}>
                    {TCG_POCKET_TRADE_REASON}
                  </div>
                )}

                {/* Same slot, same voice: either the rule blocked something or
                    the (i) was tapped. The blocked message wins, because it is
                    the answer to something that just happened. */}
                {(binderRuleNote || showTradeBinderInfo) && (
                  <div style={{ fontSize: '11.5px', lineHeight: 1.4, color: theme.textMuted, marginTop: '-2px' }}>
                    {binderRuleNote || TRADE_BINDER_EXPLAINER}
                  </div>
                )}

                {/* Legacy inline ladder, hard-disabled; superseded by PriceLadder */}
                {pricingEnabled && false && (
                  <div style={{ padding: '0', marginTop: '-4px', marginBottom: '4px' }}>
                    {/* Condition/Grade Ladder */}
                    {!isGradedCard(card.grade) && priceRow.stats && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {['nm', 'lp', 'mp', 'hp', 'dmg', 'all'].map(key => {
                          const label = { nm: 'NM', lp: 'LP', mp: 'MP', hp: 'HP', dmg: 'DMG', all: 'All' }[key];
                          const data = priceRow.stats[key];
                          if (!data?.med && !data?.avg) return null;
                          const p = data.med ?? data.avg;
                          const isActive = key === 'all'
                            ? !['nm','lp','mp','hp','dmg'].some(k => priceRow.stats[k])
                            : (card.grade || 'NM').toUpperCase() === label;
                          return (
                            <div key={key} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'transparent',
                              border: isActive ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid transparent',
                            }}>
                              <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                {label}{data.sales ? ` (${data.sales})` : ''}
                              </span>
                              <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                {p != null ? formatPrice(p) : 'N/A'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {isGradedCard(card.grade) && priceRow.stats && (() => {
                      const stats = priceRow.stats;
                      const tiers = Object.entries(stats)
                        .filter(([key]) => key !== 'all' && key !== 'raw' && key !== 'unknown')
                        .sort((a, b) => {
                          const numA = parseFloat(a[0].replace(/[^0-9.]/g, '')) || 0;
                          const numB = parseFloat(b[0].replace(/[^0-9.]/g, '')) || 0;
                          return numB - numA;
                        });
                      if (tiers.length === 0) return null;
                      const activeGrade = (card.grade || '').trim().toUpperCase();
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {tiers.map(([key, data]) => {
                            const p = data.med ?? data.avg;
                            const isActive = key.toUpperCase() === activeGrade;
                            return (
                              <div key={key} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                backgroundColor: isActive ? 'rgba(74, 222, 128, 0.1)' : 'transparent',
                                border: isActive ? '1px solid rgba(74, 222, 128, 0.3)' : '1px solid transparent',
                              }}>
                                <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                  {key}{data.sales ? ` (${data.sales})` : ''}
                                </span>
                                <span style={{ fontSize: '12px', color: isActive ? '#4ade80' : theme.textSecondary, fontWeight: isActive ? '600' : '400' }}>
                                  {p != null ? formatPrice(p) : 'N/A'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Refresh Row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '8px',
                      padding: '0 8px',
                    }}>
                      <span style={{ fontSize: '11px', color: theme.textMuted }}>
                        Updated {daysSinceRefresh(priceRow.last_refreshed_at)}
                      </span>
                      <button
                        onClick={async () => {
                          if (refreshingPrice || isRefreshLocked(priceRow)) return;
                          setRefreshingPrice(true);
                          await fetchAndCacheSingleCard({
                            name: card.name,
                            setName: card.set_name || '',
                            setNumber: card.set_number || card.card_id?.split('-').pop() || '',
                            variant: card.variant || '',
                            grade: card.grade || 'NM',
                          });
                          const newRow = await getCardPriceRow(
                            card.name,
                            card.set_name || '',
                            card.set_number || card.card_id?.split('-').pop() || '',
                          );
                          if (newRow) {
                            setPriceRow(newRow);
                            const price = getPriceForCard(newRow.stats, card.grade || 'NM', card.set_name, null, newRow.evidence, heldOut, newRow.asks);
                            if (price != null) setCardPrice({ price });
                          }
                          setRefreshingPrice(false);
                        }}
                        disabled={isRefreshLocked(priceRow) || refreshingPrice}
                        style={{
                          background: 'none',
                          border: '1px solid ' + (isRefreshLocked(priceRow) ? theme.border : theme.accent),
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          color: isRefreshLocked(priceRow) ? theme.textMuted : theme.accent,
                          cursor: isRefreshLocked(priceRow) || refreshingPrice ? 'not-allowed' : 'pointer',
                          opacity: isRefreshLocked(priceRow) ? 0.4 : 1,
                        }}
                      >
                        {refreshingPrice ? 'Refreshing...' : 'Refresh Price'}
                      </button>
                    </div>
                  </div>
                )}

                {/* The specification is a printing, and printings are chips
                    at the top of the card now, in the colour the catalogue
                    gives them. A second row saying the same word was the last
                    place anybody looked. */}

                {/* Status, era and binder all had labelled rows here. Status
                    and era moved up into the identity block; binder is a
                    control, and lives in the action card with the other two. */}


              </>
            )}
          </div>
        </div>


        {/* Action Cards. None of them belong in a negotiation: they either
            change the card or publish it. */}
        {!dealMode && (
        <div style={componentStyles.actionCards}>
          {browsingOnly && isPocketCard ? (
            /* A Pocket card being browsed has no action on it at all. It cannot
               be wished for, because anyone who opens the app already has it for
               free, and the invitation to do exactly that is further down, in
               the block that stands where pricing would. */
            null
          ) : browsingOnly ? (
            /* Wish List Action for TCGdex cards */
            <div style={{
              ...componentStyles.actionCard,
              ...(isWished ? {
                backgroundColor: 'rgba(250, 204, 21, 0.08)',
                border: '1px solid rgba(250, 204, 21, 0.3)',
              } : {}),
            }}>
              <div style={componentStyles.actionCardHeader}>
                <div style={{
                  ...componentStyles.actionCardIcon,
                  backgroundColor: isWished ? '#facc15' : 'rgba(250, 204, 21, 0.15)',
                  color: isWished ? '#000' : '#facc15',
                }}>
                  {isWished ? <HeartFilledIcon /> : <HeartIcon />}
                </div>
                <span style={{
                  ...componentStyles.actionCardTitle,
                  color: isWished ? '#facc15' : '#ffffff',
                }}>
                  {isWished ? 'In Wish List' : 'Wish List'}
                </span>
              </div>
              <div style={componentStyles.actionCardContent}>
                {isOwnCard && (
                  <div style={{
                    ...componentStyles.actionCardRow,
                    marginBottom: '12px',
                  }}>
                    <span style={{ color: '#0ea5e9', fontSize: '13px', fontWeight: '500' }}>
                      You own this card
                    </span>
                  </div>
                )}
                {isWished ? (
                  <button
                    style={{
                      ...componentStyles.actionCardBtnPrimary,
                      backgroundColor: 'rgba(250, 204, 21, 0.15)',
                      border: '1px solid rgba(250, 204, 21, 0.3)',
                      color: '#facc15',
                    }}
                    onClick={handleRemoveFromWishList}
                    disabled={removingFromWishList}
                  >
                    {removingFromWishList ? 'Removing...' : 'Remove from Wish List'}
                  </button>
                ) : (
                  <button
                    style={{
                      ...componentStyles.actionCardBtnPrimary,
                      backgroundColor: '#facc15',
                      color: '#000',
                      opacity: isTopTenFull(wishListCount) ? 0.5 : 1,
                    }}
                    onClick={handleAddToWishList}
                    disabled={addingToWishList || isTopTenFull(wishListCount)}
                  >
                    {isTopTenFull(wishListCount)
                      ? `${topTenFullLabel()} (${TOP_TEN_LIMIT}/${TOP_TEN_LIMIT})`
                      : addingToWishList
                        ? 'Adding...'
                        : `Add to ${TOP_TEN_LABEL} (${wishListCount}/${TOP_TEN_LIMIT})`}
                  </button>
                )}
              </div>
            </div>
          ) : canEdit ? (
            <>
              {/* One card, three things you can do to your own card. It used to
                  be two cards with icon headers reading "Post Status" and
                  "Collection", which is the app's filing system rather than
                  anything the owner is thinking about. */}
              <div style={{ ...componentStyles.actionCard, padding: '16px' }}>
                <div style={componentStyles.splitRow}>
                  <div style={componentStyles.splitHalf}>
                    {daysUntilRepost > 0 ? (
                      <div style={{ ...componentStyles.mergedRow, padding: '0 2px' }}>
                        <div style={componentStyles.postedStatus}>
                          <ClockIcon />
                          <span>Posted, {daysUntilRepost}d</span>
                        </div>
                        <button style={componentStyles.trashBtn} onClick={handleRemovePost}>
                          <TrashIcon />
                        </button>
                      </div>
                    ) : (
                      <button
                        style={componentStyles.actionCardBtnPrimary}
                        onClick={handleOpenPostModal}
                      >
                        Post this card
                      </button>
                    )}
                  </div>

                  {/* Always here, whether or not the card has been anywhere. A
                      button that came and went would leave the one beside it
                      changing width from card to card, and an empty journey is
                      worth saying out loud on its own screen. */}
                  <div style={componentStyles.splitHalf}>
                    <button
                      style={componentStyles.actionCardBtnSecondary}
                      onClick={() => navigate(`/card/${card.id}/journey`)}
                    >
                      Trade Journey
                    </button>
                  </div>
                </div>
              </div>

            </>
          ) : (
            <>
              {/* Trade status card. Not for a catalogue entry: nobody owns it,
                  so "Personal collection - not for trade" is a statement about
                  a collection that does not exist. */}
              {/* Someone else's TCG Pocket card. The padlock row below says
                  "personal collection", which is a choice the owner made; for
                  a digital card it is not a choice, so it gets its own line. */}
              {isTcgCard ? null : isPocketCard ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                }}>
                  {/* Named rather than priced here: this row is about where a
                      swap happens, so the product's name leads it. */}
                  <TcgPocketLine
                    theme={theme}
                    isDark={isDark}
                    label={TCG_POCKET_LABEL}
                    note={TCG_POCKET_TRADE_SHORT}
                  />
                </div>
              ) : card.trading_status === 'TRADE' ? (
                <div style={tradeRequestSent ? componentStyles.actionCardTradeRequested : componentStyles.actionCardTrade}>
                  <div style={componentStyles.tradeCardContent}>
                    <div style={tradeRequestSent ? componentStyles.tradeCardIconRequested : componentStyles.tradeCardIcon}>
                      {tradeRequestSent ? <CheckIcon /> : <TradeIcon />}
                    </div>
                    {/* About the person, not this card. "Waiting on them"
                        rather than "waiting on this card", because the same
                        line shows on every card they own. */}
                    <div style={componentStyles.tradeCardInfo}>
                      <span style={tradeRequestSent ? componentStyles.tradeCardTitleRequested : componentStyles.tradeCardTitle}>
                        {tradeRequestSent
                          ? 'Chat requested'
                          : `Open to trading`}
                      </span>
                      <span style={componentStyles.tradeCardSub}>
                        {tradeRequestSent
                          ? `Waiting on ${cardOwner?.username || 'them'}`
                          : `Start a chat with ${cardOwner?.username || 'this trader'}`}
                      </span>
                    </div>
                  </div>
                  {!tradeRequestSent && (
                    <button
                      style={componentStyles.tradeRequestBtn}
                      onClick={handleTradeButtonClick}
                      disabled={sendingRequest}
                    >
                      {sendingRequest ? 'Sending...' : 'Start a chat'}
                    </button>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600 }}>
                    Personal collection
                  </span>
                  <span style={{ color: theme.textDisabled, fontSize: 11 }}>
                    not for trade
                  </span>
                </div>
              )}

              {/* Wish List Card. Absent for a TCG Pocket card: a wish list is
                  a list of cards you want given, bought or traded to you, and
                  none of those is how a free in-app card is obtained. The
                  invitation to download the app sits further down, in the block
                  that replaces the pricing section. */}
              {!isPocketCard && (
              <div style={componentStyles.wishlistCardContainer}>
                <div
                  style={{
                    ...componentStyles.actionCardWishlist,
                    ...(isWished ? {
                      backgroundColor: 'rgba(250, 204, 21, 0.15)',
                      border: '1px solid rgba(250, 204, 21, 0.3)',
                    } : {}),
                    flex: 1,
                  }}
                  onClick={isWished ? handleRemoveFromWishList : handleAddToWishList}
                >
                  <div style={{
                    ...componentStyles.wishlistIcon,
                    backgroundColor: isWished ? '#facc15' : 'rgba(250, 204, 21, 0.15)',
                    color: isWished ? '#000' : '#facc15',
                  }}>
                    {isWished ? <HeartFilledIcon /> : <HeartIcon />}
                  </div>
                  <span style={{
                    ...componentStyles.wishlistText,
                    color: isWished ? '#facc15' : '#ffffff',
                  }}>
                    {addingToWishList ? 'Adding...'
                      : removingFromWishList ? 'Removing...'
                      : isWished ? `In ${TOP_TEN_LABEL}`
                      : isTopTenFull(wishListCount) ? topTenFullLabel()
                      : `Add to ${TOP_TEN_LABEL}`}
                  </span>
                </div>
                <button
                  style={componentStyles.viewWishListBtn}
                  onClick={() => navigate('/cards?tab=wishlist&set=all')}
                >
                  View List
                </button>
              </div>
              )}
            </>
          )}

        </div>
        )}

        {/* Pricing - always present. It used to render only once tiers arrived,
            so on a slow card it popped in late or never appeared at all, which
            reads as broken. The section and its skeleton are there from the
            first paint; the numbers fill in. */}
        {/* Pricing shows for a card you are only looking at, too. A card in the
            catalogue or on a wish list is exactly the one you want a number
            for before you buy it. What stays owner-only is the chat thread,
            which is keyed to a card in a collection. */}
        {isPocketCard && (
          <div style={componentStyles.priceSection}>
            {/* No "Pricing" heading, and no price surface of any kind. A heading
                over this block would frame a free card as an entry in a pricing
                table that happens to be empty, when the card simply does not
                belong in that table. What stands here instead is the one nice
                thing there is to say about it. */}
            <TcgPocketBanner
              theme={theme}
              isDark={isDark}
              title={TCG_POCKET_FREE_TITLE}
              reason={TCG_POCKET_FREE_BODY}
            />
            {/* Chase still has plenty to say about a Pocket card - what it is,
                where it came from, how it plays. Only the number is missing,
                so only the chart goes. No "report pricing" either: there is no
                pricing here to be wrong. */}
            {pricingEnabled && !browsingOnly && (
              <div style={{ ...componentStyles.chaseRow, gridTemplateColumns: '1fr', marginTop: '13px', marginBottom: 0 }}>
                <AskChase
                  ready
                  hasThread={hasThread}
                  onResume={() => setChatDoor('resume')}
                  onPick={(door) => { setChatDoor(door); setHasThread(true); }}
                  onComingSoon={() => setComingSoon(true)}
                  theme={theme}
                  isDark={isDark}
                />
              </div>
            )}
          </div>
        )}

        {pricingEnabled && !isPocketCard && (
          <div style={componentStyles.priceSection}>
            {/* Thirty days of history on the left, Chase on the right. That is
                the whole resting footprint; both open downward on tap. This
                replaced a one-line "CardChase read" that computed an opinion
                from numbers already on screen and told nobody anything. */}
            {cardPrice?.source && (
              <div style={componentStyles.priceSourceNote}>
                First price from {SOURCE_LABELS[cardPrice.source]}.
                {cardPrice.source !== preferredSource
                  && ` ${SOURCE_LABELS[preferredSource]} has nothing for this condition.`}
                {cardPrice.estimated && ' Estimated from the nearby conditions.'}
              </div>
            )}

            <div style={componentStyles.chaseRow}>
              <PriceHistory
                evidence={priceRow?.evidence}
                tierKey={selectedTier?.key}
                tierLabel={selectedTier?.label}
                theme={theme}
                isDark={isDark}
                open={historyOpen}
                onToggle={() => setHistoryOpen((v) => !v)}
                pending={!evidenceReady}
                hasPrice={Boolean(priceTiers)}
              />
              {!browsingOnly && (
                <AskChase
                  ready={Boolean(priceTiers) || priceTiersFailed}
                  hasThread={hasThread}
                  onResume={() => setChatDoor('resume')}
                  onPick={(door) => { setChatDoor(door); setHasThread(true); }}
                  onComingSoon={() => setComingSoon(true)}
                  theme={theme}
                  isDark={isDark}
                />
              )}
            </div>

            {priceTiers ? (
              <PriceLadder
                tiers={priceTiers}
                sources={priceSources}
                card={card}
                grade={card.grade}
                isGraded={isGradedCard(card.grade)}
                theme={theme}
                evidence={priceRow?.evidence}
                evidencePending={!evidenceReady}
                onSelectTier={setSelectedTier}
                onNeedRawSources={requestRawSources}
                isDark={isDark}
                heldOut={heldOut}
                onOpenBreakdown={(key, src) => { setBreakdownTier(key); setBreakdownSource(src || 'eBay sold'); }}
              />
            ) : priceTiersFailed ? (
              <>
              <div style={componentStyles.priceSectionTitle}>Pricing</div>
              <div style={componentStyles.priceEmpty}>
                No sales recorded for this card yet.
              </div>
              </>
            ) : (
              <div>
                <div style={componentStyles.priceSectionTitle}>Pricing</div>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} style={componentStyles.priceSkeleton}>
                    <span style={{ ...componentStyles.skelBar, width: '38px' }} />
                    <span style={{ ...componentStyles.skelBar, flex: 1, maxWidth: '120px' }} />
                    <span style={{ ...componentStyles.skelBar, width: '54px' }} />
                  </div>
                ))}
              </div>
            )}

            {/* Trigger B. Only offered once the pricing block has settled - asking
                someone to report a number that is still loading is noise. */}
            {(priceTiers || priceTiersFailed) && (
              <button
                style={componentStyles.reportPricingBtn}
                onClick={() => { haptics.tap(); setReportSheetOpen(true); }}
              >
                Something wrong with this pricing?
              </button>
            )}
          </div>
        )}

        {/* The old eBay and TCGplayer buttons lived here. Both searches now sit
            in the pricing rows themselves, next to the number they belong to,
            so choosing a condition is not a separate three tap detour. */}

        {/* Room for the deal bar, which is fixed and would otherwise cover the
            last thing on the page. */}
        {dealMode && <div style={{ height: 108 }} />}

      </div>

      {/* The way back with a number. Everything above is the argument; this is
          the conclusion, and it is editable because the figure two people shake
          on is rarely one the market printed exactly. */}
      {dealMode && (
        <div style={componentStyles.dealBar}>
          <div style={componentStyles.dealBarField}>
            <span style={componentStyles.dealBarDollar}>$</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={dealFigure}
              placeholder="0.00"
              aria-label="Agreed price"
              onChange={(e) => setDealFigure(e.target.value)}
              style={componentStyles.dealBarInput}
            />
          </div>
          <button
            style={{
              ...componentStyles.dealBarBtn,
              ...(Number.isFinite(parseFloat(dealFigure)) && parseFloat(dealFigure) >= 0
                ? {} : { opacity: 0.4 }),
            }}
            disabled={!(Number.isFinite(parseFloat(dealFigure)) && parseFloat(dealFigure) >= 0)}
            onClick={() => {
              haptics.success();
              offerPrice(dealMode.cardId, parseFloat(dealFigure));
              handleBack();
            }}
          >
            Use this price
          </button>
        </div>
      )}

      <ReportPricingSheet
        isOpen={reportSheetOpen}
        onClose={() => setReportSheetOpen(false)}
        card={card}
        context={() => buildReportContext({
          tiers: priceTiers,
          sources: priceSources,
          row: priceRow,
          selectedTier,
          card,
          failed: priceTiersFailed,
          isTcgCard,
        })}
        theme={theme}
      />

      {comingSoon && <div style={componentStyles.comingSoon}>Coming soon</div>}

      {/* Full screen rather than a sheet: it is a conversation, not a control,
          and the card underneath has nothing left to contribute once it starts. */}
      <ChaseChat
        open={Boolean(chatDoor)}
        card={card}
        door={chatDoor}
        selectedTier={selectedTier?.label || null}
        onClose={() => setChatDoor(null)}
        theme={theme}
        isDark={isDark}
      />

      <SaleBreakdownSheet
        isOpen={Boolean(breakdownTier)}
        onClose={() => setBreakdownTier(null)}
        card={card}
        evidence={priceRow?.evidence}
        tier={breakdownTier}
        source={breakdownSource}
        prefs={pricingPrefs?.sale_window || undefined}
        onSavePrefs={saveSaleWindow}
        onHeldOutChange={setHeldOut}
        theme={theme}
        isDark={isDark}
      />

      {/* Card name search sheet - replaces the old "Find more of this card" button */}
      <BottomSheet
        open={namePickerOpen}
        onClose={() => setNamePickerOpen(false)}
        overlayStyle={componentStyles.namePickerOverlay}
        sheetStyle={componentStyles.namePickerSheet}
        label="Search this card"
      >
            <div style={componentStyles.namePickerTitle}>{card.name}</div>
            <button style={componentStyles.namePickerOption} onClick={handleSearchThisCard}>
              <span style={componentStyles.namePickerOptionMain}>Search this exact card</span>
              <span style={componentStyles.namePickerOptionSub}>
                {card.set_name}{card.set_number ? ` #${card.set_number}` : ''}
              </span>
            </button>
            <button style={componentStyles.namePickerOption} onClick={handleSearchByName}>
              <span style={componentStyles.namePickerOptionMain}>Search other {card.name}</span>
              <span style={componentStyles.namePickerOptionSub}>Any set, any number</span>
            </button>
            <button style={componentStyles.namePickerCancel} onClick={() => setNamePickerOpen(false)}>
              Cancel
            </button>
      </BottomSheet>
      </div>{/* End main content wrapper */}

      {/* Photo Viewer Modal */}
      <AnimatePresence>
        {photoViewerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.photoViewerOverlay}
          >
            {/* Header */}
            <div style={componentStyles.photoViewerHeader}>
              <button
                style={componentStyles.photoViewerCloseBtn}
                onClick={handleClosePhotoViewer}
              >
                <CloseIcon />
              </button>

              <span style={componentStyles.photoViewerTitle}>
                {cardPhotos[selectedPhotoIndex]?.label || 'Photo'}
              </span>

              {/* Grid/Zoom Toggle - only show if multiple photos */}
              {hasMultiplePhotos && (
                <button
                  style={componentStyles.photoViewerModeBtn}
                  onClick={() => setPhotoViewMode(photoViewMode === 'zoom' ? 'grid' : 'zoom')}
                >
                  {photoViewMode === 'zoom' ? <GridIcon /> : <MaximizeIcon />}
                </button>
              )}
              {!hasMultiplePhotos && <div style={{ width: 44 }} />}
            </div>

            {/* Content */}
            {photoViewMode === 'zoom' ? (
              /* Zoom Mode - Single Image with Swipe */
              <div
                style={componentStyles.zoomModeContainer}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={() => handleTouchEnd(cardPhotos)}
              >
                {/* Left Arrow */}
                {hasMultiplePhotos && selectedPhotoIndex > 0 && (
                  <button style={componentStyles.photoNavArrowLeft} onClick={goToPrevPhoto}>
                    <ChevronLeftIcon color="#ffffff" />
                  </button>
                )}

                <TransformWrapper
                  key={selectedPhotoIndex}
                  initialScale={1}
                  minScale={1}
                  maxScale={4}
                  centerOnInit={true}
                  wheel={{ disabled: true }}
                  doubleClick={{ mode: 'toggle', step: 2 }}
                >
                  <TransformComponent>
                    <motion.img
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                      src={cardPhotos[selectedPhotoIndex]?.url}
                      alt={cardPhotos[selectedPhotoIndex]?.label}
                      style={componentStyles.zoomedImage}
                    />
                  </TransformComponent>
                </TransformWrapper>

                {/* Right Arrow */}
                {hasMultiplePhotos && selectedPhotoIndex < cardPhotos.length - 1 && (
                  <button style={componentStyles.photoNavArrowRight} onClick={() => goToNextPhoto(cardPhotos)}>
                    <ChevronRightIcon color="#ffffff" />
                  </button>
                )}

                {/* Photo Navigation Dots */}
                {hasMultiplePhotos && (
                  <div style={componentStyles.photoDots}>
                    {cardPhotos.map((_, index) => (
                      <button
                        key={index}
                        style={{
                          ...componentStyles.photoDot,
                          ...(index === selectedPhotoIndex ? componentStyles.photoDotActive : {}),
                        }}
                        onClick={() => setSelectedPhotoIndex(index)}
                      />
                    ))}
                  </div>
                )}

                {/* Photo Counter */}
                {hasMultiplePhotos && (
                  <div style={componentStyles.photoCounter}>
                    {selectedPhotoIndex + 1} / {cardPhotos.length}
                  </div>
                )}
              </div>
            ) : (
              /* Grid Mode - All Photos */
              <div style={componentStyles.gridModeContainer}>
                <div style={componentStyles.photoGrid}>
                  {cardPhotos.map((photo, index) => (
                    <div
                      key={index}
                      style={{
                        ...componentStyles.photoGridItem,
                        ...(index === selectedPhotoIndex ? componentStyles.photoGridItemActive : {}),
                      }}
                      onClick={() => {
                        setSelectedPhotoIndex(index);
                        setPhotoViewMode('zoom');
                      }}
                    >
                      <img
                        src={sizedImageUrl(photo.url, IMG.STRIP)}
                        alt={photo.label}
                        loading="lazy"
                        style={componentStyles.photoGridImage}
                      />
                      <span style={componentStyles.photoGridLabel}>{photo.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Coming Soon Popup */}
      <AnimatePresence>
      </AnimatePresence>

      {/* Report Success Modal */}
      <AnimatePresence>
        {reportSuccessOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.popupOverlay}
            onClick={() => {
              setReportSuccessOpen(false);
              navigate(-1);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{
                ...componentStyles.popupModal,
                maxWidth: 320,
                textAlign: 'center',
                padding: 28,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: 64,
                height: 64,
                borderRadius: 32,
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 style={{
                fontSize: 20,
                fontWeight: 600,
                color: theme.text,
                marginBottom: 8,
              }}>
                Report Submitted
              </h3>
              <p style={{
                fontSize: 14,
                color: theme.textMuted,
                marginBottom: 24,
                lineHeight: 1.5,
              }}>
                Thank you for helping keep our community safe. Our team will review this card shortly.
              </p>
              <button
                onClick={() => {
                  setReportSuccessOpen(false);
                  navigate(-1);
                }}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  backgroundColor: theme.accent,
                  border: 'none',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Binder Picker Popup */}
      <AnimatePresence>
        {binderPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.popupOverlay}
          >
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={componentStyles.binderPickerSheet}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.binderPickerHandle} />
              <div style={componentStyles.popupHeader}>
                <h3 style={componentStyles.popupTitle}>
                  {cardBinder ? 'Change Binder' : 'Select Binder'}
                </h3>
                <button
                  style={componentStyles.popupClose}
                  onClick={() => setBinderPickerOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>

              <div style={componentStyles.binderList}>
                {binders.length === 0 ? (
                  <p style={componentStyles.noBinders}>No binders yet. Create one first!</p>
                ) : (
                  binders.map((binder) => {
                    const isSelected = selectedBinder?.id === binder.id;
                    const isPreviewing = previewBinder?.id === binder.id;

                    return (
                      <div key={binder.id}>
                        <div style={componentStyles.binderRow}>
                          <button
                            style={{
                              ...componentStyles.binderOption,
                              ...(isSelected ? componentStyles.binderOptionSelected : {}),
                            }}
                            onClick={() => setSelectedBinder(isSelected ? null : binder)}
                          >
                            <div style={{
                              ...componentStyles.binderColor,
                              backgroundColor: binder.image === 'red' ? '#ef4444' :
                                              binder.image === 'blue' ? '#3b82f6' :
                                              binder.image === 'green' ? '#22c55e' :
                                              binder.image === 'yellow' ? '#eab308' :
                                              binder.image === 'purple' ? '#a855f7' :
                                              binder.image === 'pink' ? '#ec4899' :
                                              binder.image === 'orange' ? '#f97316' :
                                              '#6b7280',
                            }} />
                            <span style={componentStyles.binderName}>{binder.name}</span>
                            {isTradeBinder(binder) && (
                              <span style={componentStyles.binderTradeTag}>For trade</span>
                            )}
                            {isSelected && <span style={componentStyles.selectedCheck}>Selected</span>}
                          </button>
                          <button
                            style={{
                              ...componentStyles.quickViewBtn,
                              ...(isPreviewing ? componentStyles.quickViewBtnActive : {}),
                            }}
                            onClick={() => handleQuickView(binder)}
                          >
                            <GridIcon />
                          </button>
                        </div>

                        {/* Quick View Preview */}
                        {isPreviewing && (
                          <div style={componentStyles.previewGrid}>
                            {previewCards.length === 0 ? (
                              <span style={componentStyles.previewEmpty}>No cards yet</span>
                            ) : (
                              previewCards.map((c) => (
                                <CardImage
                                  key={c.id}
                                  digitalImage={c.digital_image_url}
                                  userPhoto={c.original_image_url}
                                  name={c.name}
                                  theme={theme}
                                  style={componentStyles.previewCard}
                                />
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Done Button */}
              <div style={componentStyles.binderPickerFooter}>
                <button
                  style={componentStyles.doneButton}
                  onClick={handleBinderDone}
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grade Picker Modal */}
      <AnimatePresence>
        {gradePickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.gradePickerOverlay}
            onClick={() => setGradePickerOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              style={componentStyles.gradePickerModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.gradePickerHeader}>
                <h3 style={componentStyles.gradePickerTitle}>Update Condition</h3>
                <button
                  style={componentStyles.gradePickerClose}
                  onClick={() => setGradePickerOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Warning */}
              <p style={componentStyles.gradePickerWarning}>
                Misrepresenting card condition to mislead traders may result in negative reviews and account restrictions.
              </p>

              {/* Raw / Graded Toggle */}
              <div style={componentStyles.gradeTypeToggle}>
                <button
                  style={{
                    ...componentStyles.gradeTypeBtn,
                    ...(gradeType === 'raw' ? componentStyles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => setGradeType('raw')}
                >
                  Raw
                </button>
                <button
                  style={{
                    ...componentStyles.gradeTypeBtn,
                    ...(gradeType === 'graded' ? componentStyles.gradeTypeBtnActive : {}),
                  }}
                  onClick={() => setGradeType('graded')}
                >
                  Graded
                </button>
              </div>

              {/* Company Toggle (only for graded) */}
              {gradeType === 'graded' && (
                <>
                  <div style={componentStyles.companyToggle}>
                    {['PSA', 'CGC', 'BGS', 'ACE', 'TAG', 'Other'].map((company) => (
                      <button
                        key={company}
                        style={{
                          ...componentStyles.companyBtn,
                          ...(gradingCompany === company ? componentStyles.companyBtnActive : {}),
                        }}
                        onClick={() => setGradingCompany(company)}
                      >
                        {company}
                      </button>
                    ))}
                  </div>
                  {/* Custom company input for "Other" */}
                  {gradingCompany === 'Other' && (
                    <input
                      type="text"
                      placeholder="Enter company name (e.g., SGC)"
                      value={customCompany}
                      onChange={(e) => setCustomCompany(e.target.value.toUpperCase())}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        marginBottom: 12,
                        background: '#1a1a1a',
                        border: '1px solid #333',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 15,
                      }}
                    />
                  )}
                </>
              )}

              <div style={componentStyles.gradePickerOptions}>
                {getGradeOptions().map((option) => (
                  <button
                    key={option.value}
                    style={{
                      ...componentStyles.gradePickerOption,
                      ...(selectedGrade === option.value ? componentStyles.gradePickerOptionActive : {}),
                    }}
                    onClick={() => setSelectedGrade(option.value)}
                  >
                    <span>{option.label}</span>
                    {selectedGrade === option.value && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>

              {/* Done Button */}
              <div style={componentStyles.gradePickerFooter}>
                <button
                  style={componentStyles.gradePickerDoneBtn}
                  onClick={handleGradeConfirm}
                  disabled={savingGrade}
                >
                  {savingGrade ? 'Saving...' : 'Done'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Variant Picker Modal */}
      <AnimatePresence>
        {variantPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={componentStyles.gradePickerOverlay}
            onClick={() => setVariantPickerOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              style={componentStyles.gradePickerModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={componentStyles.gradePickerHeader}>
                <h3 style={componentStyles.gradePickerTitle}>Update Variant</h3>
                <button
                  style={componentStyles.gradePickerClose}
                  onClick={() => setVariantPickerOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>

              <p style={componentStyles.gradePickerWarning}>
                Only the printings this card actually comes in are listed. Pick
                what you are holding.
              </p>

              {variantAxes === null && (
                <p style={componentStyles.gradePickerWarning}>Reading the printings...</p>
              )}

              {variantAxes && variantAxes.types.length > 0 && (
                <>
                  <h3 style={{ ...componentStyles.gradePickerTitle, fontSize: 14, marginBottom: 8 }}>Finish</h3>
                  <div style={componentStyles.gradePickerOptions}>
                    {variantAxes.types.map((value) => (
                      <button
                        key={value}
                        style={{
                          ...componentStyles.gradePickerOption,
                          ...(variantDraft.type === value ? componentStyles.variantPickerOptionActive : {}),
                        }}
                        onClick={() => setVariantDraft((d) => ({ ...d, type: d.type === value ? null : value }))}
                      >
                        <span>{labelFor('type', value)}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {variantAxes && (variantAxes.subtypes.length > 0 || variantAxes.stamps.length > 0) && (
                <>
                  <h3 style={{ ...componentStyles.gradePickerTitle, fontSize: 14, margin: '16px 0 8px' }}>Printing</h3>
                  <div style={componentStyles.gradePickerOptions}>
                    {variantAxes.subtypes.map((value) => (
                      <button
                        key={`sub-${value}`}
                        style={{
                          ...componentStyles.gradePickerOption,
                          ...(variantDraft.subtype === value ? componentStyles.variantPickerOptionActive : {}),
                        }}
                        onClick={() => setVariantDraft((d) => ({ ...d, subtype: d.subtype === value ? null : value }))}
                      >
                        <span>{labelFor('subtype', value)}</span>
                      </button>
                    ))}
                    {/* Stamps are multi-select: a card can be 1st Edition AND
                        carry a pre-release stamp. */}
                    {variantAxes.stamps.map((value) => {
                      const on = (variantDraft.stamps || []).includes(value);
                      return (
                        <button
                          key={`stamp-${value}`}
                          style={{
                            ...componentStyles.gradePickerOption,
                            ...(on ? componentStyles.variantPickerOptionActive : {}),
                          }}
                          onClick={() => setVariantDraft((d) => ({
                            ...d,
                            stamps: on
                              ? (d.stamps || []).filter((x) => x !== value)
                              : [...(d.stamps || []), value],
                          }))}
                        >
                          <span>{labelFor('stamps', value)}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {variantAxes && variantAxes.count === 0 && (
                <p style={componentStyles.gradePickerWarning}>
                  The catalogue has no printings recorded for this card yet.
                </p>
              )}

              {/* Specification */}
              <div style={{ marginTop: 16 }}>
                <h3 style={{ ...componentStyles.gradePickerTitle, fontSize: 14, marginBottom: 8 }}>Specification</h3>
                <div style={componentStyles.gradePickerOptions}>
                  {[{ value: '', label: 'None' }, { value: 'Shadowless', label: 'Shadowless' }, { value: 'WOTC Stamped', label: 'WOTC Stamped' }, { value: 'Prerelease', label: 'Prerelease' }, { value: 'STAFF', label: 'STAFF' }, { value: 'Winner', label: 'Winner' }, { value: 'League Stamp', label: 'League Stamp' }, { value: 'Build-A-Bear', label: 'Build-A-Bear' }, { value: 'Error/Misprint', label: 'Error/Misprint' }].map((option) => (
                    <button
                      key={option.value}
                      style={{
                        ...componentStyles.gradePickerOption,
                        ...((card?.specification || '') === option.value ? componentStyles.variantPickerOptionActive : {}),
                      }}
                      onClick={async () => {
                        const spec = option.value || null;
                        await supabase.from('cards').update({ specification: spec }).eq('id', card.id);
                        setCard(prev => ({ ...prev, specification: spec }));
                      }}
                    >
                      <span>{option.label}</span>
                      {(card?.specification || '') === option.value && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Done Button */}
              <div style={componentStyles.gradePickerFooter}>
                <button
                  style={componentStyles.variantPickerDoneBtn}
                  onClick={handleVariantConfirm}
                  disabled={savingVariant}
                >
                  {savingVariant ? 'Saving...' : 'Done'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Card Modal */}
      <ReportCardModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        cardId={cardId}
        currentUserId={currentUserId}
        onSuccess={handleReportSuccess}
        theme={theme}
      />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={postModalOpen}
        onClose={() => setPostModalOpen(false)}
        userId={currentUserId}
        userCards={userCards}
        preSelectedCard={card}
        onSuccess={() => {
          // Reset repost cooldown after successful post
          setLastPostDate(new Date());
          setDaysUntilRepost(7);
        }}
      />

      {/* Asking to meet somebody.

          It leads with the person, not the card that got you here, because the
          card is a door and does not travel with the request. The binder is
          offered and never explained - people work out for themselves that it
          is the reason to meet, and saying it turns a discovery into a rule. */}
      <StartChatSheet
        isOpen={tradeConfirmOpen && !!card}
        username={cardOwner?.username || 'this trader'}
        avatarUrl={cardOwner?.avatar_url}
        binderName={ownerTradeBinder?.name}
        binderCount={ownerTradeBinder?.count}
        sending={sendingRequest}
        onLookAtBinder={ownerTradeBinder && card?.user_id
          ? () => {
            setTradeConfirmOpen(false);
            navigate(`/user/${card.user_id}/collection?binder=${ownerTradeBinder.id}`);
          }
          : undefined}
        onStart={executeSendTradeRequest}
        onClose={() => setTradeConfirmOpen(false)}
      />

      {/* First Trade Request Tip - shows after sending first trade request */}
      <TutorialBackdrop
        visible={currentStep === TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP}
      />
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP}
        mode="normal"
        characterImage={ChaseWaveImage}
        message={[
          "There you go!",
          "",
          "Let's see if they're interested",
          "in your collection!",
          "Here's a tip..."
        ]}
        hideButtons={false}
        yesText="Next"
        noText=""
        onYes={() => {
          handleFirstTradeRequestNext();
          navigate('/home');
        }}
      />

      {/* Add Parent Modal for viewer-only users */}
      <AddParentModal
        isOpen={showParentModal}
        onClose={() => setShowParentModal(false)}
        feature="trading"
      />

    </div>
  );
};

const styles = (theme, isDark) => ({
  dealBar: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 60,
    display: 'flex', alignItems: 'center', gap: 10,
    background: theme.bgPure, borderTop: `1px solid ${theme.border}`,
    padding: '12px 16px calc(24px + var(--safe-area-bottom, 0px))',
  },
  dealBarField: {
    display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto',
    background: theme.bgCard, border: `1px solid ${theme.border}`,
    borderRadius: 12, padding: '10px 14px', minWidth: 132,
  },
  dealBarDollar: { fontSize: 20, fontWeight: 700, color: theme.textSecondary },
  dealBarInput: {
    width: 84, border: 0, background: 'none', color: theme.text,
    fontSize: 22, fontWeight: 700, outline: 'none', fontFamily: 'inherit',
    fontVariantNumeric: 'tabular-nums',
  },
  dealBarBtn: {
    flex: 1, border: 0, borderRadius: 12, padding: '15px 12px',
    background: theme.accent, color: '#04121c', fontWeight: 700,
    fontSize: 15, fontFamily: 'inherit', cursor: 'pointer',
  },
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    background: isDark ? theme.bg : `linear-gradient(180deg, ${theme.bgCard} 0%, ${theme.bg} 150px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  errorText: {
    color: theme.textMuted,
    fontSize: '16px',
  },
  backButton: {
    padding: '12px 24px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.border}`,
    flexShrink: 0,
    gap: '12px',
  },
  headerBackBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
    flexShrink: 0,
  },
  headerCenter: {
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
  },
  factsTitleBtn: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '5px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    maxWidth: '100%',
  },
  factsName: {
    fontSize: '21px',
    lineHeight: 1.25,
    fontWeight: '500',
    letterSpacing: '-0.2px',
    color: theme.text,
    // Wrap rather than truncate: the card image is tall, so the column has the
    // height to spare and a clipped card name helps nobody.
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  factsSet: {
    fontSize: '12px',
    color: theme.textSecondary,
    marginTop: '4px',
    marginBottom: '14px',
    lineHeight: 1.5,
  },
  headerOwnerBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: '0 auto',
    maxWidth: '100%',
    cursor: 'pointer',
  },
  headerOwnerAvatar: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    flexShrink: 0,
    background: 'linear-gradient(135deg, #0ea5e9, #8b5cf6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '700',
    overflow: 'hidden',
  },
  headerOwnerImg: { width: '100%', height: '100%', objectFit: 'cover' },
  headerOwnerSub: {
    display: 'block',
    fontSize: '10px',
    color: theme.textSecondary,
    textAlign: 'left',
  },
  headerTitleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: '0 auto',
    color: theme.text,
    cursor: 'pointer',
    maxWidth: '100%',
  },
  namePickerOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 11000,
    padding: '16px',
    paddingBottom: 'calc(24px + var(--safe-area-bottom, 0px))',
  },
  namePickerSheet: {
    width: '100%',
    maxWidth: '420px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '16px',
    padding: '10px',
  },
  namePickerTitle: {
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '600',
    padding: '8px 10px 10px',
  },
  namePickerOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    width: '100%',
    textAlign: 'left',
    background: theme.bgSheet,
    border: `1px solid ${theme.border}`,
    borderRadius: '11px',
    padding: '12px',
    marginBottom: '7px',
    cursor: 'pointer',
  },
  namePickerOptionMain: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  namePickerOptionSub: {
    color: theme.textSecondary,
    fontSize: '11.5px',
  },
  namePickerCancel: {
    width: '100%',
    background: 'none',
    border: 'none',
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '600',
    padding: '11px',
    cursor: 'pointer',
  },
  headerTitle: {
    color: theme.text,
    fontSize: '16px',
    fontWeight: '700',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '2px',
  },
  headerSet: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  headerNumber: {
    color: '#0ea5e9',
    fontSize: '12px',
    fontWeight: '600',
  },
  menuWrapper: {
    position: 'relative',
    flexShrink: 0,
  },
  headerMenuBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textMuted,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 20px var(--nav-clearance) 20px',
  },
  // Fixed art, flexible info column. The art used to be a 140px flex basis
  // whose height came from an aspect ratio, which made the hero's height a
  // function of the viewport. The reference pins it.
  topSection: {
    display: 'flex',
    gap: '14px',
    marginBottom: '24px',
  },
  cardImageWrapper: {
    position: 'relative',
    flex: '0 0 108px',
    width: '108px',
    height: '150px',
    cursor: 'pointer',
  },
  cardImage: {
    width: '108px',
    height: '150px',
    borderRadius: '10px',
    objectFit: 'cover',
    boxShadow: isDark
      ? '0 4px 16px rgba(0, 0, 0, 0.4)'
      : '0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
  },
  zoomHint: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '28px',
    height: '28px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
  },
  priceSkeleton: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '11px 12px',
    borderRadius: '10px',
    marginBottom: '5px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.bgCard,
  },
  skelBar: {
    height: '10px',
    borderRadius: '5px',
    backgroundColor: theme.border,
    opacity: 0.65,
    animation: 'cc-pulse 1.4s ease-in-out infinite',
  },
  priceEmpty: {
    fontSize: '12.5px',
    color: theme.textSecondary,
    padding: '14px 2px',
  },
  chaseRow: {
    display: 'grid',
    // The chart carries a number, a change and a line; Chase carries a face and
    // two words. Equal columns would leave one of them padded with air.
    gridTemplateColumns: '1.5fr 1fr',
    gap: '8px',
    alignItems: 'stretch',
    marginBottom: '13px',
  },
  comingSoon: {
    position: 'fixed',
    left: '50%',
    bottom: 'calc(84px + var(--safe-area-bottom, 0px))',
    transform: 'translateX(-50%)',
    zIndex: 950,
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    fontSize: '12.5px',
    padding: '10px 16px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
  },
  reportPricingBtn: {
    display: 'block',
    width: '100%',
    marginTop: '10px',
    padding: '9px 0',
    border: 0,
    background: 'transparent',
    color: theme.textMuted,
    fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', lineHeight: 1,
    textAlign: 'center',
    cursor: 'pointer',
  },
  priceSection: {
    // The read block is the first thing in here, and with no top margin it sat
    // flush against the Collection or Wish List card above it, reading as part
    // of that card rather than as the start of pricing.
    //
    // The gap above is deliberately larger than the gap between the read and the
    // table below it. The read and the numbers are one group; the card above is
    // a different one, and the spacing should say so.
    marginTop: '22px',
    marginBottom: '12px',
  },
  priceSectionTitle: {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
    color: theme.textSecondary,
    marginBottom: '9px',
  },
  priceSourceNote: {
    fontSize: '11px',
    lineHeight: 1.5,
    color: theme.textSecondary,
    marginBottom: '10px',
  },
  marketRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  marketBtn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
    padding: '11px 8px',
    borderRadius: '12px',
    backgroundColor: theme.bgSheet,
    border: `1px solid ${theme.border}`,
    textDecoration: 'none',
  },
  marketBtnEbay: {
    backgroundColor: 'rgba(14, 165, 233, 0.10)',
    border: `1px solid ${theme.accent}`,
  },
  marketBtnMain: {
    fontSize: '13px',
    fontWeight: '700',
    color: theme.text,
  },
  marketBtnSub: {
    fontSize: '10px',
    color: theme.textSecondary,
  },
  variantStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 14px',
    marginBottom: '12px',
    backgroundColor: 'rgba(234, 179, 8, 0.10)',
    border: '1px solid rgba(234, 179, 8, 0.35)',
    borderRadius: '12px',
    cursor: 'pointer',
    color: theme.text,
  },
  variantStripLabel: {
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: theme.textSecondary,
  },
  variantStripValue: {
    flex: 1,
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '700',
    color: theme.text,
  },
  quickInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    padding: '7px 0',
    borderTop: `1px solid ${theme.border}`,
    minHeight: '34px',
  },
  infoLabel: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  infoValue: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  infoValueSmall: {
    color: theme.textMuted,
    fontSize: '13px',
  },
  ebayLink: {
    color: '#3b82f6',
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  infoRowTappable: {
    // No padding or negative margin here. infoRow now owns the row's spacing
    // and draws the divider; this used to add 8px of padding and pull itself
    // back up by 8px to compensate, which collided with the row above and
    // dragged the divider straight through the text.
    cursor: 'pointer',
    borderRadius: '8px',
  },
  infoValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  statusTrade: {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#4ade80',
  },
  statusPC: {
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    color: '#a78bfa',
  },
  detailsButton: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    backgroundColor: theme.bgCard,
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '12px',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: isDark ? 'none' : '0 1px 4px rgba(0, 0, 0, 0.04)',
  },
  chefsLabButton: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
  },
  ownerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    // Now sits directly under the header rather than below the card, so it
    // leads the screen instead of trailing it (item 21).
    marginBottom: '14px',
    padding: '12px 16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: isDark ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  ownerAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: theme.border,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  ownerAvatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  ownerAvatarPlaceholder: {
    color: theme.textMuted,
    fontSize: '14px',
    fontWeight: '600',
  },
  ownerName: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
    flex: 1,
  },
  ownerArrow: {
    color: '#0ea5e9',
    fontSize: '13px',
    fontWeight: '500',
  },
  // Action Cards Styles
  actionCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '20px',
  },
  actionCard: {
    backgroundColor: theme.bgCard,
    borderRadius: '14px',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    boxShadow: isDark ? 'none' : '0 2px 12px rgba(0, 0, 0, 0.06)',
  },
  actionCardChef: {
    backgroundColor: 'rgba(139, 92, 246, 0.08)',
    borderRadius: '16px',
    border: '1px solid rgba(139, 92, 246, 0.2)',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  chefIcon: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
  },
  chefDescription: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  actionCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 14px',
    borderBottom: `1px solid ${theme.border}`,
  },
  actionCardIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#0ea5e9',
  },
  actionCardTitle: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  actionCardContent: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  actionCardRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // Hairline-separated rows inside one card, no icon header. The separator is
  // a border on the row rather than a spacer element, so a row that does not
  // render takes its divider with it.
  mergedInner: { display: 'flex', flexDirection: 'column' },
  mergedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    minHeight: '44px',
    padding: '12px 2px',
    fontSize: '13px',
  },
  mergedRowDivided: { borderBottom: `0.5px solid ${theme.borderSubtle}` },
  mergedRowLast: { padding: '12px 2px 2px' },
  mergedRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  chipRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  factChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    border: `0.5px solid ${theme.borderHover}`,
    background: theme.bgInput,
    color: theme.textMuted,
    fontFamily: 'inherit',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 1.2,
    padding: '6px 12px',
    borderRadius: '999px',
    cursor: 'pointer',
  },
  // Display only. No border, no chevron, not a control - the editable status
  // sits in the action card, and two ways to change one thing on one screen is
  // how a user stops trusting either.
  heroStatusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 1.2,
    padding: '6px 12px',
    borderRadius: '999px',
  },
  actionCardLabel: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  // Post and Trade Journey, equal halves of one row. A gap rather than a rule
  // between them: they are two separate actions, not one control split in two.
  splitRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    alignItems: 'center',
  },
  splitHalf: { minWidth: 0 },
  // The journey is somewhere to go, not the thing to do with the card, so it
  // reads as an outline against the filled blue beside it.
  actionCardBtnSecondary: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    border: '1px solid #2f6d92',
    color: '#7fc4ec',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  actionCardBtnPrimary: {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: '#1f9bf0',
    border: 'none',
    // Dark on blue, not white. White on this blue is about 2.6:1 and reads as a
    // disabled control; the dark ink is what makes it the primary action.
    color: '#04263d',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  actionCardBtnDanger: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },
  postedStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: '6px',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    color: '#4ade80',
    fontSize: '12px',
    fontWeight: '500',
  },
  trashBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  statusToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: 0,
    border: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '400',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  statusToggleTrade: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    color: '#4ade80',
  },
  statusTogglePC: {
    backgroundColor: 'rgba(139, 92, 246, 0.15)',
    color: '#a78bfa',
  },
  binderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  // Plain value plus a chevron, matching the Status row beside it. It used to
  // be an outlined button, which made two rows in one card look like two
  // different kinds of control.
  binderSelectBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: 0,
    background: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    color: theme.text,
    fontSize: '13px',
    fontWeight: '400',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  binderRemoveBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  // Trade Card (for non-owners)
  actionCardTrade: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  actionCardTradeRequested: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  },
  tradeCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  tradeCardIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
  },
  tradeCardIconRequested: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ef4444',
  },
  tradeCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  tradeCardTitle: {
    color: '#f87171',
    fontSize: '13px',
    fontWeight: '600',
  },
  tradeCardTitleRequested: {
    color: '#ef4444',
    fontSize: '13px',
    fontWeight: '600',
  },
  tradeCardSub: {
    color: theme.textSecondary,
    fontSize: '11px',
  },
  tradeRequestBtn: {
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: '#ef4444',
    border: 'none',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    whiteSpace: 'nowrap',
  },
  // Wish List Card
  actionCardWishlist: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.08)' : 'rgba(249, 115, 22, 0.12)',
    borderRadius: '10px',
    border: isDark ? '1px solid rgba(249, 115, 22, 0.2)' : '1px solid rgba(249, 115, 22, 0.3)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  wishlistCardContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'stretch',
  },
  viewWishListBtn: {
    padding: '10px 14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.textMuted,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    WebkitTapHighlightColor: 'transparent',
  },
  wishlistIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(234, 88, 12, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isDark ? '#f97316' : '#c2410c',
  },
  wishlistText: {
    color: isDark ? '#fb923c' : '#c2410c',
    fontSize: '13px',
    fontWeight: '600',
  },
  // Search Card
  actionCardSearch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: theme.bgCard,
    borderRadius: '10px',
    border: `1px solid ${theme.border}`,
    color: theme.textMuted,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  // Card Navigation Bar
  cardNavBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0 16px',
    marginTop: '12px',
    padding: '12px 14px',
    backgroundColor: theme.bgSheet,
    borderRadius: '14px',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.06)',
    boxShadow: isDark ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  cardNavBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '76px',
    gap: '4px',
    padding: '8px 6px',
    backgroundColor: theme.bgCard,
    borderRadius: '8px',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.08)',
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    maxWidth: '45%',
    overflow: 'hidden',
  },
  cardNavText: {
    color: theme.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '11px',
  },
  cardNavCounter: {
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardNavBinderLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: theme.accent,
    fontWeight: '650',
    cursor: 'pointer',
  },
  cardNavSource: {
    color: theme.text,
    fontWeight: '600',
  },
  cardNavPlaceholder: {
    // Must match cardNavBtn's width exactly, or the counter drifts off centre
    // whenever one side is missing (e.g. at "1 of 67", which has no Prev).
    width: '76px',
  },
  // Photo Viewer Styles
  photoViewerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000000',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'var(--safe-area-top, 0px)',
    paddingBottom: 'var(--safe-area-bottom, 0px)',
    paddingLeft: 'var(--safe-area-left, 0px)',
    paddingRight: 'var(--safe-area-right, 0px)',
  },
  photoViewerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    flexShrink: 0,
  },
  photoViewerCloseBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
  },
  photoViewerTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
  },
  photoViewerModeBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
  },
  zoomModeContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '40px',
    position: 'relative',
    overflow: 'hidden',
  },
  zoomedImage: {
    maxWidth: '90vw',
    maxHeight: '70vh',
    objectFit: 'contain',
    borderRadius: '16px',
    touchAction: 'pinch-zoom',
    boxShadow: isDark
      ? '0 8px 32px rgba(0, 0, 0, 0.6)'
      : '0 12px 48px rgba(0, 0, 0, 0.2)',
  },
  photoDots: {
    position: 'absolute',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  },
  photoDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  photoDotActive: {
    backgroundColor: '#0ea5e9',
    transform: 'scale(1.2)',
  },
  photoNavArrowLeft: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    zIndex: 10,
  },
  photoNavArrowRight: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    zIndex: 10,
  },
  photoCounter: {
    position: 'absolute',
    bottom: '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: '#ffffff',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
  },
  gridModeContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 16px 20px',
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  photoGridItem: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'all 0.2s ease',
  },
  photoGridItemActive: {
    borderColor: '#0ea5e9',
  },
  photoGridImage: {
    width: '100%',
    aspectRatio: '0.714',
    objectFit: 'cover',
  },
  photoGridLabel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '500',
    textAlign: 'center',
  },
  // Popup styles
  popupOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  popupModal: {
    backgroundColor: theme.bgSheet,
    borderRadius: '20px',
    width: '100%',
    maxWidth: '360px',
    maxHeight: '80vh',
    overflow: 'hidden',
    border: isDark ? `1px solid ${theme.border}` : '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
  popupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  popupTitle: {
    color: theme.text,
    fontSize: '17px',
    fontWeight: '600',
    margin: 0,
  },
  popupClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  popupContent: {
    padding: '20px',
    overflowY: 'auto',
    maxHeight: '60vh',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  detailLabel: {
    color: theme.textSecondary,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailValue: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
  },
  // Binder picker styles
  binderPickerSheet: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.bgSheet,
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    maxHeight: '70vh',
    overflow: 'hidden',
    boxShadow: isDark ? '0 -4px 24px rgba(0, 0, 0, 0.5)' : '0 -8px 32px rgba(0, 0, 0, 0.12)',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    borderBottom: 'none',
  },
  binderPickerHandle: {
    width: '36px',
    height: '4px',
    backgroundColor: theme.borderHover,
    borderRadius: '2px',
    margin: '12px auto 0',
  },
  binderList: {
    padding: '16px',
    overflowY: 'auto',
    maxHeight: '45vh',
  },
  binderRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px',
  },
  binderOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  binderOptionSelected: {
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderColor: '#0ea5e9',
  },
  quickViewBtn: {
    width: '48px',
    height: '48px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  },
  quickViewBtnActive: {
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
    borderColor: '#0ea5e9',
    color: '#0ea5e9',
  },
  binderColor: {
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    flexShrink: 0,
  },
  binderName: {
    flex: 1,
    color: theme.text,
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'left',
  },
  selectedCheck: {
    color: '#0ea5e9',
    fontSize: '12px',
    fontWeight: '600',
  },
  binderTradeTag: {
    flex: 'none',
    padding: '1.5px 7px',
    borderRadius: '999px',
    border: '0.5px solid rgba(74, 222, 128, 0.45)',
    background: 'rgba(74, 222, 128, 0.14)',
    color: '#4ade80',
    fontSize: '9.5px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  previewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
    padding: '12px',
    backgroundColor: theme.bg,
    borderRadius: '10px',
    marginBottom: '10px',
    marginTop: '-4px',
  },
  previewCard: {
    width: '100%',
    aspectRatio: '245 / 342',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  previewEmpty: {
    gridColumn: '1 / -1',
    color: theme.textMuted,
    fontSize: '12px',
    textAlign: 'center',
    padding: '20px',
  },
  binderPickerFooter: {
    padding: '16px',
    borderTop: `1px solid ${theme.border}`,
  },
  doneButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#0ea5e9',
    border: 'none',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  noBinders: {
    color: theme.textSecondary,
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px',
    margin: 0,
  },
  // Grade Picker Styles
  gradePickerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  gradePickerModal: {
    width: '100%',
    maxWidth: '500px',
    backgroundColor: theme.bgSheet,
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    maxHeight: '60vh',
    marginBottom: 'calc(80px + var(--safe-area-bottom, 0px))',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isDark ? '0 -4px 24px rgba(0, 0, 0, 0.5)' : '0 -8px 32px rgba(0, 0, 0, 0.12)',
    border: isDark ? 'none' : '1px solid rgba(0, 0, 0, 0.06)',
    borderBottom: 'none',
  },
  gradePickerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  gradePickerTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  gradePickerClose: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  gradePickerOptions: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  gradePickerOption: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.text,
    fontSize: '15px',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  gradePickerOptionActive: {
    color: '#0ea5e9',
    backgroundColor: 'rgba(14, 165, 233, 0.1)',
  },
  variantPickerOptionActive: {
    color: theme.warning || '#f59e0b',
    backgroundColor: theme.warningBg || 'rgba(245, 158, 11, 0.1)',
  },
  variantPickerDoneBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.warning || '#f59e0b',
    border: 'none',
    borderRadius: '12px',
    color: '#000',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  detailItemTappable: {
    cursor: 'pointer',
  },
  editHint: {
    fontSize: '11px',
    opacity: 0.7,
    fontWeight: '400',
  },
  gradeTypeToggle: {
    display: 'flex',
    backgroundColor: theme.bgCard,
    borderRadius: '10px',
    padding: '4px',
    margin: '0 16px 12px',
  },
  gradeTypeBtn: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  gradeTypeBtnActive: {
    backgroundColor: '#0ea5e9',
    color: '#ffffff',
  },
  companyToggle: {
    display: 'flex',
    backgroundColor: theme.bgCard,
    borderRadius: '10px',
    padding: '4px',
    margin: '0 16px 12px',
  },
  companyBtn: {
    flex: 1,
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  companyBtnActive: {
    backgroundColor: '#0ea5e9',
    color: '#ffffff',
  },
  gradePickerWarning: {
    color: '#f97316',
    fontSize: '12px',
    lineHeight: '1.4',
    margin: '0 16px 12px',
    padding: '10px 12px',
    backgroundColor: 'rgba(249, 115, 22, 0.1)',
    borderRadius: '8px',
    border: '1px solid rgba(249, 115, 22, 0.2)',
  },
  gradePickerFooter: {
    padding: '12px 16px',
    borderTop: `1px solid ${theme.border}`,
  },
  gradePickerDoneBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '10px',
    color: theme.text,
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
});


export default CardDetail;
